import React, { useMemo } from "react";
import type { Theme } from "../lib/theme";
import { displayStatus } from "../lib/statusLink";
import { scopeBudget, scopeLineItems, scopeCoverage, isNarrowed } from "../lib/scopeBudget";
import ScopeWorkPicker, { divisionsOf } from "../components/ScopeWorkPicker";
import type { Division, ItemState, BuyoutItem, ShowToast } from "../types";
import type { BidScope, BidPartner, Bid, LevelingAdjustment } from "../projectData";
import { ALL_DIVISIONS } from "../projectData";
import { walkItems, isClarification } from "../lib/calculations";
import type { DivTotals } from "../lib/calculations";
import { computePackagePipeline, computePlugVsBid, STANDARD_LEVELING_ITEMS } from "../lib/bidPipeline";
import EmptyState from "../components/EmptyState";
import * as Icon from "../components/OEIcons";

const FONT = "'Inter','Avenir Next','Century Gothic',sans-serif";

function bidTotal(bid: Bid): number {
  return bid.items.reduce((s, i) => (i.qty || 0) * (i.unitCost || 0) + s, 0);
}

interface BidsTabProps {
  T: Theme;
  isMobile: boolean;
  isTablet: boolean;
  divisions: Division[];
  itemState: Record<string, ItemState>;
  bids: Bid[];
  setBids: React.Dispatch<React.SetStateAction<Bid[]>>;
  bidScopes: BidScope[];
  setBidScopes: React.Dispatch<React.SetStateAction<BidScope[]>>;
  bidPartners: BidPartner[];
  setBidPartners: React.Dispatch<React.SetStateAction<BidPartner[]>>;
  levelingAdjustments: LevelingAdjustment[];
  setLevelingAdjustments: React.Dispatch<React.SetStateAction<LevelingAdjustment[]>>;
  bidAlignments: Record<string, Record<string, string>>;
  setBidAlignments: React.Dispatch<React.SetStateAction<Record<string, Record<string, string>>>>;
  activeBidScope: string | null;
  setActiveBidScope: React.Dispatch<React.SetStateAction<string | null>>;
  bidSubTab: "compare" | "history";
  setBidSubTab: React.Dispatch<React.SetStateAction<"compare" | "history">>;
  bidNavMode: "list" | "tree";
  setBidNavMode: React.Dispatch<React.SetStateAction<"list" | "tree">>;
  bidSnapModalId: string | null;
  setBidSnapModalId: React.Dispatch<React.SetStateAction<string | null>>;
  snapExpandedDivs: Set<string>;
  setSnapExpandedDivs: React.Dispatch<React.SetStateAction<Set<string>>>;
  tradeSources: {id: string; tradePartner: string; estimateDate: string; notes: string}[];
  setTradeSources: React.Dispatch<React.SetStateAction<{id: string; tradePartner: string; estimateDate: string; notes: string}[]>>;
  bidsSnapshot: () => void;
  /** Opens the BuildingConnected leveling import. */
  onImportBcLeveling: () => void;
  /** Opens the print dialog on the bid comparison sheet. */
  onPrintComparison: () => void;
  showToast: ShowToast;
  uid: () => string;
  fmt: (n: number) => string;
  setAnalysisLeft: React.Dispatch<React.SetStateAction<string>>;
  setAnalysisRight: React.Dispatch<React.SetStateAction<string | null>>;
  setActiveTab: (tab: string) => void;
  projectSnapshots: any[];
  snapshot: () => void;
  divTotals: DivTotals;
  editingScopeId: string | null;
  setEditingScopeId: React.Dispatch<React.SetStateAction<string | null>>;
  editingScopeName: string;
  setEditingScopeName: React.Dispatch<React.SetStateAction<string>>;
  showNewScope: boolean;
  setShowNewScope: React.Dispatch<React.SetStateAction<boolean>>;
  newScopeName: string;
  setNewScopeName: React.Dispatch<React.SetStateAction<string>>;
  newScopeDivIds: string[];
  setNewScopeDivIds: React.Dispatch<React.SetStateAction<string[]>>;
  orgPartnersCount: number;
  showPartnersPanel: boolean;
  setShowPartnersPanel: React.Dispatch<React.SetStateAction<boolean>>;
  bidTreeExpanded: string[];
  setBidTreeExpanded: React.Dispatch<React.SetStateAction<string[]>>;
  bidPdfCache: React.MutableRefObject<Map<string, string>>;
  bidChartHover: {bid: Bid; x: number; y: number} | null;
  setBidChartHover: React.Dispatch<React.SetStateAction<{bid: Bid; x: number; y: number} | null>>;
  bidChartScopeFilter: string | null;
  setBidChartScopeFilter: React.Dispatch<React.SetStateAction<string | null>>;
  bidChartRangeMode: "all" | "iteration" | "custom";
  setBidChartRangeMode: React.Dispatch<React.SetStateAction<"all" | "iteration" | "custom">>;
  bidChartCustomStart: string;
  setBidChartCustomStart: React.Dispatch<React.SetStateAction<string>>;
  bidChartCustomEnd: string;
  setBidChartCustomEnd: React.Dispatch<React.SetStateAction<string>>;
  bidChartW: number;
  setBidChartW: React.Dispatch<React.SetStateAction<number>>;
  collapsedHistoryPartners: Set<string>;
  setCollapsedHistoryPartners: React.Dispatch<React.SetStateAction<Set<string>>>;
  setBidImportScopeId: React.Dispatch<React.SetStateAction<string | null>>;
  setShowBidImport: React.Dispatch<React.SetStateAction<boolean>>;
  showDocScopePicker: boolean;
  setShowDocScopePicker: React.Dispatch<React.SetStateAction<boolean>>;
  comparePdfIds: string[];
  setComparePdfIds: React.Dispatch<React.SetStateAction<string[]>>;
  hiddenPartners: Record<string, string[]>;
  setHiddenPartners: React.Dispatch<React.SetStateAction<Record<string, string[]>>>;
  dragBidItemIds: string[] | null;
  setDragBidItemIds: React.Dispatch<React.SetStateAction<string[] | null>>;
  dropTargetEstItemId: string | null;
  setDropTargetEstItemId: React.Dispatch<React.SetStateAction<string | null>>;
  expandedAlignRows: Set<string>;
  setExpandedAlignRows: React.Dispatch<React.SetStateAction<Set<string>>>;
  openBidPdfViewer: (bidIds: string[]) => Promise<void>;
  viewBidPdf: (bid: Bid) => Promise<void>;
  setShowSowEditor: React.Dispatch<React.SetStateAction<any>>;
  setItemState: (fn: (prev: Record<string, ItemState>) => Record<string, ItemState>) => void;
  setDivisionsRaw: React.Dispatch<React.SetStateAction<Division[]>>;
  buyoutItems: BuyoutItem[];
  onAwardBid: (args: {scopeName: string; scopeDivisionIds: string[]; partnerName: string; bidId: string; leveledTotal: number; budget: number}) => void;
}

export default function BidsTab({
  T, isMobile, isTablet,
  divisions, itemState,
  bids, setBids, bidScopes, setBidScopes, bidPartners, setBidPartners,
  levelingAdjustments, setLevelingAdjustments,
  bidAlignments, setBidAlignments,
  activeBidScope, setActiveBidScope,
  bidSubTab, setBidSubTab, bidNavMode, setBidNavMode,
  bidSnapModalId, setBidSnapModalId, snapExpandedDivs, setSnapExpandedDivs,
  tradeSources, setTradeSources,
  bidsSnapshot, onImportBcLeveling, onPrintComparison, showToast, uid, fmt,
  setAnalysisLeft, setAnalysisRight, setActiveTab,
  projectSnapshots, snapshot, divTotals,
  editingScopeId, setEditingScopeId, editingScopeName, setEditingScopeName,
  showNewScope, setShowNewScope, newScopeName, setNewScopeName,
  newScopeDivIds, setNewScopeDivIds, orgPartnersCount, showPartnersPanel, setShowPartnersPanel,
  bidTreeExpanded, setBidTreeExpanded, bidPdfCache,
  bidChartHover, setBidChartHover, bidChartScopeFilter, setBidChartScopeFilter,
  bidChartRangeMode, setBidChartRangeMode, bidChartCustomStart, setBidChartCustomStart,
  bidChartCustomEnd, setBidChartCustomEnd, bidChartW, setBidChartW,
  collapsedHistoryPartners, setCollapsedHistoryPartners,
  setBidImportScopeId, setShowBidImport,
  showDocScopePicker, setShowDocScopePicker,
  comparePdfIds, setComparePdfIds, hiddenPartners, setHiddenPartners,
  dragBidItemIds, setDragBidItemIds, dropTargetEstItemId, setDropTargetEstItemId,
  expandedAlignRows, setExpandedAlignRows,
  openBidPdfViewer, viewBidPdf, setShowSowEditor,
  setItemState, setDivisionsRaw,
  buyoutItems, onAwardBid,
}: BidsTabProps) {
// Which import source to use is a choice behind the one import button, so it
// is local to this tab rather than another flag threaded through the app.
const [showImportMenu,setShowImportMenu]=React.useState(false);
// What a new scope covers, as subcategory ids. Local because it is transient
// form state; the scope itself carries subIds once created.
const [newScopeSubIds,setNewScopeSubIds]=React.useState<string[]>([]);
// Collapsed sidebar, for when the comparison needs the width. A rail rather
// than nothing, so there is always a way back.
const [sidebarOpen,setSidebarOpen]=React.useState(true);

// The working pane's own top padding, which the sticky header has to climb
// back over.
//
// A sticky offset resolves against the scrollport's PADDING edge, and that
// padding is inside the clip region -- so `top: 0` pinned the header 20px
// down and left a 20px strip above it where the rows scrolled past in plain
// sight, half a line item floating over the column names. Pinning by minus
// the padding puts the header flush with the top of the pane, where its own
// background covers the strip.
const PANE_PAD_T = isMobile ? 14 : 20;

// The tab fills the window and no more.
//
// Without this the scope sidebar grew to its content -- 967px of scope list
// in a 900px window -- so the whole tab overflowed and the APP scrolled it.
// Scrolling the comparison therefore carried the sidebar off the top of the
// screen with it. Bounded here, the sidebar and the working pane each scroll
// their own contents and neither moves the other.
//
// Against the parent, not the viewport: the app already gives this view a
// bounded box, and guessing the chrome height in vh left 59px of overflow
// that still dragged the sidebar up the screen.
const TAB_H = "100%";
// Migrate tradeSources → bidPartners once (keep for archive)
if(tradeSources.length>0&&bidPartners.length===0){
  setBidPartners(tradeSources.map(s=>({id:s.id,name:s.tradePartner,trade:"",contact:"",notes:s.notes})));
}
// Auto-generate scopes from divisions on first visit
if(bidScopes.length===0&&divisions.length>0){
  const scopes=divisions.map(d=>({id:uid(),name:d.name,divisionIds:[d.id]}));
  setBidScopes(scopes);setActiveBidScope(scopes[0]?.id||null);
}

const BCOLORS=["#4a8fbf","#d4a76a","#a0c8a0","#c8a0d4","#d4706a","#6ad4c8"];
// bidSubTab + setBidSubTab are top-level state — no shim needed

// ── Scope-level aggregations ──
// Stale-bid thresholds: yellow at 30+ days (worth noting), red at 60+ days
const scopeStats=bidScopes.map(scope=>{
  const scopeBids=bids.filter(b=>b.scopeId===scope.id);
  const activeBids=scopeBids.filter(b=>b.status==="Active");
  const totals=activeBids.map(b=>bidTotal(b));
  const lowest=totals.length?Math.min(...totals):0;
  const pipeline=computePackagePipeline(scope,bids,levelingAdjustments,buyoutItems||[]);
  return{scope,bidCount:activeBids.length,allBidCount:scopeBids.length,lowest,activeBids,scopeBids,pipeline};
});

const activeScope=bidScopes.find(s=>s.id===activeBidScope);
const activeScopeStat=scopeStats.find(s=>s.scope.id===activeBidScope);


return(
<div className="fade-in" style={{display:"flex",flex:1,overflow:"hidden",height:TAB_H,minHeight:0}}>
  {/* ── Left: Scope Sidebar ── */}
  {/* Same collapse as the Estimate sidebar: the panel narrows to a rail and
      a chevron at the top turns it back, rather than a second element
      appearing in its place with a control of its own design. One gesture,
      one place, wherever a sidebar collapses. */}
  <div style={{width:sidebarOpen?240:36,background:T.bgPanel,borderRight:`1px solid ${T.borderPrimary}`,
    overflow:"hidden",flexShrink:0,paddingTop:6,transition:"width .18s ease",
    display:"flex",flexDirection:"column" as any,minHeight:0}}>
    {/* The collapse sits ON the header row, in line with the list/tree view
        toggle, rather than on a row of its own above it. A control that acts
        on the panel belongs beside the other controls that act on the panel.
        Collapsed, it is the only thing left, so it centres. */}
    <div style={{display:"flex",alignItems:"center",gap:4,padding:"6px 8px 5px",
      borderBottom:`1px solid ${T.borderPrimary}`,flexShrink:0,
      justifyContent:sidebarOpen?"flex-start":"center"}}>
      <button onClick={()=>setSidebarOpen(o=>!o)} title={sidebarOpen?"Collapse sidebar":"Expand sidebar"}
        style={{background:"none",border:"none",color:T.textWeak,cursor:"pointer",padding:"2px 2px",
          display:"flex",alignItems:"center",fontSize:13,opacity:.5,flexShrink:0,order:sidebarOpen?2:0}}
        onMouseEnter={e=>e.currentTarget.style.opacity="1"} onMouseLeave={e=>e.currentTarget.style.opacity=".5"}>
        {sidebarOpen?<Icon.ChevronLeft size={13}/>:<Icon.ChevronRight size={13}/>}
      </button>
      {sidebarOpen&&(activeBidScope&&bidNavMode==="list"?(
        <button onClick={()=>setActiveBidScope(null)}
          style={{display:"flex",alignItems:"center",gap:4,flex:1,background:"none",border:"none",cursor:"pointer",fontFamily:FONT,padding:"2px 4px",borderRadius:3,textAlign:"left" as any}}
          onMouseEnter={e=>e.currentTarget.style.background=T.bgHover}
          onMouseLeave={e=>e.currentTarget.style.background="none"}>
          <span style={{fontSize:12,color:T.textWeak}}><Icon.ArrowLeft size={13}/></span>
          <span style={{fontSize:10,fontWeight:700,color:T.borderAccent,letterSpacing:".06em",textTransform:"uppercase" as any}}>All Scopes</span>
        </button>
      ):(
        <span style={{flex:1,fontSize:10,color:T.textWeak,letterSpacing:".1em",textTransform:"uppercase" as any,padding:"2px 4px"}}>{bidNavMode==="tree"?"Bid Explorer":"Trade Scopes"}</span>
      ))}
      {/* List / Tree toggle */}
      {sidebarOpen&&<div style={{display:"flex",background:T.bgApp,border:`1px solid ${T.borderPrimary}`,borderRadius:3,overflow:"hidden",flexShrink:0}}>
        {(["list","tree"] as const).map(mode=>{
          const active=bidNavMode===mode;
          return(
            <button key={mode} onClick={()=>setBidNavMode(mode)} title={mode==="list"?"List view":"Tree view"}
              style={{padding:"3px 8px",fontSize:10,fontWeight:active?700:400,border:"none",cursor:"pointer",fontFamily:FONT,
                background:active?T.borderAccent:"transparent",color:active?T.textOnGold:T.textWeak,transition:"all .15s",lineHeight:1.2}}>
              {mode==="list"?<Icon.Menu size={13}/>:<Icon.Grid size={13}/>}
            </button>
          );
        })}
      </div>}
    </div>

    {/* Everything below the header hides when the panel is a rail. */}
    <div style={{display:sidebarOpen?"flex":"none",flexDirection:"column" as any,minHeight:0,flex:1}}>
    {/* Scope actions — above the list, shared by both views */}
    <div style={{display:"flex",gap:4,padding:"6px 8px",borderBottom:`1px solid ${T.borderPrimary}`,flexShrink:0}}>
      <button onClick={()=>{setShowNewScope(true);setNewScopeName("");setNewScopeDivIds([]);}}
        title="Create a trade scope to collect bids against"
        style={{flex:1,minWidth:0,padding:"5px 6px",background:T.bgAccentSubtle,border:`1px solid ${T.borderAccent}55`,borderRadius:3,color:T.borderAccent,cursor:"pointer",fontSize:10,fontWeight:700,fontFamily:FONT,whiteSpace:"nowrap" as any}}>
        + Trade Scope
      </button>
      <button onClick={()=>setShowPartnersPanel(true)}
        title="Trade partners in your organization directory"
        style={{flexShrink:0,padding:"5px 8px",background:"none",border:`1px solid ${T.borderSecondary}`,borderRadius:3,color:T.textWeak,cursor:"pointer",fontSize:10,fontFamily:FONT,display:"inline-flex",alignItems:"center",gap:4,whiteSpace:"nowrap" as any}}>
        <Icon.Users size={13}/> {orgPartnersCount}
      </button>
    </div>

    {/* ── LIST VIEW (original) ── */}
    {bidNavMode==="list"&&(
      <div style={{flex:1,overflowY:"auto",display:"flex",flexDirection:"column" as any}}>
        <div style={{flex:1,overflowY:"auto"}}>
          {scopeStats.map(({scope,bidCount,lowest,pipeline},i)=>{
            const isActive=activeBidScope===scope.id;
            const isEditing=editingScopeId===scope.id;
            return(
              <div key={scope.id}
                style={{position:"relative",borderLeft:isActive?`2px solid ${T.borderAccent}`:"2px solid transparent",background:isActive?T.bgAccentSubtle:"transparent",transition:"all .15s"}}
                onMouseEnter={e=>{const ic=e.currentTarget.querySelector(".scope-actions") as HTMLElement;if(ic)ic.style.opacity="1";}}
                onMouseLeave={e=>{const ic=e.currentTarget.querySelector(".scope-actions") as HTMLElement;if(ic)ic.style.opacity="0";}}>
                {isEditing?(
                  <form onSubmit={e=>{e.preventDefault();if(editingScopeName.trim())setBidScopes(p=>p.map(s=>s.id===scope.id?{...s,name:editingScopeName.trim()}:s));setEditingScopeId(null);}}
                    style={{padding:"6px 8px"}}>
                    <input autoFocus value={editingScopeName} onChange={e=>setEditingScopeName(e.target.value)}
                      onKeyDown={e=>{if(e.key==="Escape")setEditingScopeId(null);}}
                      style={{width:"100%",background:T.bgInput,border:`1px solid ${T.borderAccent}`,borderRadius:3,color:T.textPrimary,fontSize:11,padding:"4px 8px",fontFamily:FONT,boxSizing:"border-box" as any}}/>
                    <div style={{display:"flex",gap:4,marginTop:4}}>
                      <button type="submit" style={{flex:1,fontSize:10,padding:"3px 0",background:T.borderAccent,color:T.textOnGold,border:"none",borderRadius:3,cursor:"pointer",fontFamily:FONT}}>Save</button>
                      <button type="button" onClick={()=>setEditingScopeId(null)} style={{flex:1,fontSize:10,padding:"3px 0",background:"none",color:T.textWeak,border:`1px solid ${T.borderSecondary}`,borderRadius:3,cursor:"pointer",fontFamily:FONT}}>Cancel</button>
                    </div>
                  </form>
                ):(
                  <button onClick={()=>{setActiveBidScope(scope.id);if(bidCount>0)setBidSubTab("compare");}}
                    style={{cursor:"pointer",display:"flex",justifyContent:"space-between",alignItems:"center",width:"100%",padding:"10px 14px",border:"none",textAlign:"left",
                      background:"transparent",color:isActive?T.borderAccent:T.textTertiary,
                      fontFamily:FONT,fontSize:11}}>
                    <div style={{overflow:"hidden",minWidth:0,flex:1}}>
                      <div style={{display:"flex",alignItems:"center",gap:6}}>
                        <span style={{display:"inline-block",width:6,height:6,borderRadius:3,background:BCOLORS[i%BCOLORS.length],flexShrink:0}}/>
                        <span style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",fontWeight:600}}>{scope.name}</span>
                        {pipeline.awarded&&<span style={{fontSize:10,padding:"1px 5px",borderRadius:10,background:`${T.positive}22`,border:`1px solid ${T.positive}55`,color:T.positive,fontWeight:700,flexShrink:0,letterSpacing:".05em"}}>Awarded</span>}
                      </div>
                      <div style={{display:"flex",alignItems:"center",gap:4,marginTop:3,marginLeft:12,flexWrap:"wrap" as any}}>
                        {(scope.divisionIds||[]).map(did=>{const div=ALL_DIVISIONS.find(d=>d.id===did);return div?(
                          <span key={did} style={{fontSize:10,padding:"1px 4px",borderRadius:3,background:isActive?`${T.borderAccent}22`:`${T.textMuted}18`,color:isActive?T.borderAccent:T.textMuted,fontWeight:600,letterSpacing:".04em"}}>{div.code}</span>
                        ):null;})}
                        {bidCount>0&&<span style={{fontSize:10,color:T.textWeak}}>{bidCount} bid{bidCount!==1?"s":""}</span>}
                      </div>
                    </div>
                  </button>
                )}
                {/* Scope action icons (show on hover) */}
                {!isEditing&&(
                  <div className="scope-actions" style={{position:"absolute",right:6,top:"50%",transform:"translateY(-50%)",display:"flex",gap:2,opacity:0,transition:"opacity .15s",pointerEvents:"auto"}}>
                    <button title="Rename scope" onClick={e=>{e.stopPropagation();setEditingScopeId(scope.id);setEditingScopeName(scope.name);}}
                      style={{background:"none",border:"none",color:T.textWeak,cursor:"pointer",fontSize:11,padding:"2px 4px",borderRadius:3,lineHeight:1}}><Icon.Edit size={12}/></button>
                    <button title="Delete scope" onClick={e=>{e.stopPropagation();
                      const n=bids.filter(b=>b.scopeId===scope.id).length;
                      bidsSnapshot();
                      setBidScopes(p=>p.filter(s=>s.id!==scope.id));setBids(p=>p.filter(b=>b.scopeId!==scope.id));
                      if(activeBidScope===scope.id)setActiveBidScope(null);
                      showToast(`Deleted "${scope.name}"${n?` and ${n} bid${n===1?"":"s"} on it`:""}`,6000,true);}}
                      style={{background:"none",border:"none",color:T.textWeak,cursor:"pointer",fontSize:10,padding:"2px 4px",borderRadius:3,lineHeight:1}}><Icon.X size={10}/></button>
                  </div>
                )}
              </div>
            );
          })}
          {bidScopes.length===0&&(
            <div style={{padding:"10px 14px",fontSize:10,color:T.textMuted,fontStyle:"italic"}}>No scopes defined</div>
          )}
          {/* Inline new scope form */}
          {showNewScope&&(
            <div style={{padding:"8px 10px",borderTop:`1px solid ${T.borderPrimary}`,marginTop:4}}>
              <div style={{fontSize:10,color:T.textWeak,marginBottom:4,letterSpacing:".08em",textTransform:"uppercase" as any}}>New Trade Scope</div>
              <input autoFocus placeholder="Scope name…" value={newScopeName} onChange={e=>setNewScopeName(e.target.value)}
                onKeyDown={e=>{if(e.key==="Escape"){setShowNewScope(false);setNewScopeName("");setNewScopeDivIds([]);setNewScopeSubIds([]);}}}
                style={{width:"100%",background:T.bgInput,border:`1px solid ${T.borderAccent}`,borderRadius:3,color:T.textPrimary,fontSize:11,padding:"4px 8px",fontFamily:FONT,boxSizing:"border-box" as any,marginBottom:6}}/>
              <ScopeWorkPicker T={T} divisions={divisions} selected={newScopeSubIds} setSelected={setNewScopeSubIds}/>
              <div style={{display:"flex",gap:4}}>
                <button onClick={()=>{
                  const name=newScopeName.trim();
                  if(!name)return;
                  // divisionIds stays populated from whichever divisions the
                  // chosen subcategories belong to, so anything still reading
                  // it (the Scope of Work generator, buyout matching) keeps
                  // working. subIds is what the budget uses.
                  const newScope:BidScope={id:uid(),name,divisionIds:divisionsOf(divisions,newScopeSubIds),subIds:newScopeSubIds};
                  setBidScopes(p=>[...p,newScope]);
                  setActiveBidScope(newScope.id);
                  setShowNewScope(false);setNewScopeName("");setNewScopeDivIds([]);setNewScopeSubIds([]);
                }} style={{flex:1,fontSize:10,padding:"4px 0",background:T.borderAccent,color:T.textOnGold,border:"none",borderRadius:3,cursor:"pointer",fontFamily:FONT,fontWeight:700}}>
                  + Add
                </button>
                <button onClick={()=>{setShowNewScope(false);setNewScopeName("");setNewScopeDivIds([]);setNewScopeSubIds([]);}}
                  style={{flex:1,fontSize:10,padding:"4px 0",background:"none",color:T.textWeak,border:`1px solid ${T.borderSecondary}`,borderRadius:3,cursor:"pointer",fontFamily:FONT}}>Cancel</button>
              </div>
            </div>
          )}
        </div>
      </div>
    )}

    {/* ── TREE VIEW ── */}
    {bidNavMode==="tree"&&(
      <div style={{flex:1,overflowY:"auto",display:"flex",flexDirection:"column" as any}}>
        <div style={{flex:1,overflowY:"auto"}}>
          {(()=>{
            // Build tree: all CSI divisions that have at least one scope
            const divsWithScopes=ALL_DIVISIONS.filter(d=>bidScopes.some(s=>(s.divisionIds||[]).includes(d.id)));
            // Scopes without any division mapping shown at the bottom ungrouped
            const orphanScopes=bidScopes.filter(s=>!(s.divisionIds||[]).length);

            const renderScopeNode=(scope:BidScope,indent:number)=>{
              const scopeNodeId=`scope-${scope.id}`;
              const isScopeOpen=bidTreeExpanded.includes(scopeNodeId);
              const isActive=activeBidScope===scope.id;
              const scopeBids=bids.filter(b=>b.scopeId===scope.id);
              const partnerIds=[...new Set(scopeBids.map(b=>b.partnerId))];
              const hasChildren=partnerIds.length>0;
              return(
                <div key={scope.id}>
                  <div
                    style={{display:"flex",alignItems:"center",gap:4,
                      paddingLeft:indent,paddingRight:8,paddingTop:5,paddingBottom:5,
                      cursor:"pointer",userSelect:"none" as any,
                      borderLeft:isActive?`2px solid ${T.borderAccent}`:"2px solid transparent",
                      background:isActive?T.bgAccentSubtle:"transparent",transition:"background .12s"}}
                    onClick={()=>{
                      setActiveBidScope(scope.id);
                      if(hasChildren)setBidTreeExpanded(p=>isScopeOpen?p.filter(x=>x!==scopeNodeId):[...p,scopeNodeId]);
                    }}
                    onMouseEnter={e=>{if(!isActive)(e.currentTarget as HTMLElement).style.background=T.bgHover;}}
                    onMouseLeave={e=>{if(!isActive)(e.currentTarget as HTMLElement).style.background="transparent";}}>
                    <span style={{fontSize:10,color:T.textWeak,transition:"transform .15s",display:"inline-block",
                      transform:isScopeOpen?"rotate(90deg)":"rotate(0deg)",flexShrink:0,lineHeight:1,
                      opacity:hasChildren?1:0}}>▶</span>
                    <span style={{fontSize:12,lineHeight:1,flexShrink:0}}><Icon.Folder size={12}/></span>
                    <span style={{fontSize:11,flex:1,minWidth:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" as any,
                      color:isActive?T.borderAccent:T.textTertiary,fontWeight:isActive?700:500}}>
                      {scope.name}
                    </span>
                    {scopeBids.filter(b=>b.status==="Active").length>0&&(
                      <span style={{fontSize:10,padding:"1px 4px",borderRadius:10,background:`${T.borderAccent}18`,color:T.borderAccent,fontWeight:600,flexShrink:0}}>
                        {scopeBids.filter(b=>b.status==="Active").length}
                      </span>
                    )}
                  </div>
                  {/* Partner nodes */}
                  {isScopeOpen&&partnerIds.map(pid=>{
                    const partner=bidPartners.find(p=>p.id===pid);
                    const partnerNodeId=`partner-${scope.id}-${pid}`;
                    const isPartnerOpen=bidTreeExpanded.includes(partnerNodeId);
                    const partnerBids=scopeBids.filter(b=>b.partnerId===pid).sort((a,b2)=>b2.version-a.version);
                    const activeBid=partnerBids.find(b=>b.status==="Active");
                    return(
                      <div key={pid}>
                        <div
                          style={{display:"flex",alignItems:"center",gap:4,
                            paddingLeft:indent+16,paddingRight:8,paddingTop:4,paddingBottom:4,
                            cursor:"pointer",userSelect:"none" as any,background:"transparent",transition:"background .1s"}}
                          onClick={()=>{
                            setActiveBidScope(scope.id);
                            setBidSubTab("history");
                            setBidTreeExpanded(p=>isPartnerOpen?p.filter(x=>x!==partnerNodeId):[...p,partnerNodeId]);
                          }}
                          onMouseEnter={e=>(e.currentTarget as HTMLElement).style.background=T.bgHover}
                          onMouseLeave={e=>(e.currentTarget as HTMLElement).style.background="transparent"}>
                          <span style={{fontSize:10,color:T.textWeak,transition:"transform .15s",display:"inline-block",
                            transform:isPartnerOpen?"rotate(90deg)":"rotate(0deg)",flexShrink:0,lineHeight:1,
                            opacity:partnerBids.length>1?1:.2}}>▶</span>
                          <span style={{fontSize:12,lineHeight:1,flexShrink:0}}><Icon.User size={12}/></span>
                          <div style={{flex:1,minWidth:0}}>
                            <div style={{fontSize:11,color:T.textSecondary,fontWeight:500,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" as any}}>
                              {partner?.name||"Unknown"}
                            </div>
                            {activeBid&&(
                              <div style={{fontSize:10,color:T.textMuted,marginTop:1}}>
                                v{activeBid.version} · {fmt(bidTotal(activeBid))}
                              </div>
                            )}
                          </div>
                        </div>
                        {/* Bid file nodes */}
                        {isPartnerOpen&&partnerBids.map(bid=>{
                          const hasFile=!!(bid.pdfUrl||bid.pdfStoragePath||bidPdfCache.current.has(bid.id));
                          const isActiveBid=bid.status==="Active";
                          return(
                            <div key={bid.id}
                              style={{display:"flex",alignItems:"center",gap:4,
                                paddingLeft:indent+32,paddingRight:8,paddingTop:3,paddingBottom:3,
                                cursor:"pointer",background:"transparent",transition:"background .1s"}}
                              onClick={()=>{
                                setActiveBidScope(scope.id);
                                setBidSubTab("history");
                              }}
                              onMouseEnter={e=>(e.currentTarget as HTMLElement).style.background=T.bgHover}
                              onMouseLeave={e=>(e.currentTarget as HTMLElement).style.background="transparent"}>
                              <span style={{fontSize:11,lineHeight:1,flexShrink:0,opacity:hasFile?1:.5}}>{hasFile?<Icon.FileText size={11}/>:<Icon.Clipboard size={11}/>}</span>
                              <div style={{flex:1,minWidth:0}}>
                                <div style={{fontSize:10,color:isActiveBid?T.textSecondary:T.textMuted,fontWeight:isActiveBid?600:400,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" as any}}>
                                  {bid.pdfName||`v${bid.version} · ${bid.date?.slice(0,10)||"—"}`}
                                </div>
                                <div style={{display:"flex",alignItems:"center",gap:5,marginTop:1}}>
                                  <span style={{fontSize:10,color:isActiveBid?T.borderAccent:T.textMuted,fontVariantNumeric:"tabular-nums" as any}}>
                                    {fmt(bidTotal(bid))}
                                  </span>
                                  <span style={{fontSize:10,padding:"0px 4px",borderRadius:10,fontWeight:600,
                                    background:isActiveBid?`${T.positive}20`:`${T.textMuted}18`,
                                    color:isActiveBid?T.positive:T.textMuted}}>
                                    {isActiveBid?"Active":bid.status}
                                  </span>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              );
            };

            return(
              <>
                {divsWithScopes.map(div=>{
                  const divNodeId=`div-${div.id}`;
                  const isDivOpen=bidTreeExpanded.includes(divNodeId);
                  const divScopes=bidScopes.filter(s=>(s.divisionIds||[]).includes(div.id));
                  const divActiveBidCount=divScopes.reduce((n,s)=>n+bids.filter(b=>b.scopeId===s.id&&b.status==="Active").length,0);
                  return(
                    <div key={div.id}>
                      <div
                        style={{display:"flex",alignItems:"center",gap:5,padding:"6px 8px",cursor:"pointer",
                          userSelect:"none" as any,background:"transparent",transition:"background .1s"}}
                        onClick={()=>setBidTreeExpanded(p=>isDivOpen?p.filter(x=>x!==divNodeId):[...p,divNodeId])}
                        onMouseEnter={e=>(e.currentTarget as HTMLElement).style.background=T.bgHover}
                        onMouseLeave={e=>(e.currentTarget as HTMLElement).style.background="transparent"}>
                        <span style={{fontSize:10,color:T.textWeak,transition:"transform .15s",display:"inline-block",
                          transform:isDivOpen?"rotate(90deg)":"rotate(0deg)",flexShrink:0,lineHeight:1}}>▶</span>
                        <span style={{fontSize:10,fontWeight:700,color:T.textMuted,letterSpacing:".03em",flexShrink:0}}>{div.code}</span>
                        <span style={{fontSize:11,color:T.textTertiary,fontWeight:600,flex:1,minWidth:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" as any}}>
                          {div.name.replace(/^Div(ision)? \d+ [–-] /,"")}
                        </span>
                        {divActiveBidCount>0&&(
                          <span style={{fontSize:10,padding:"1px 4px",borderRadius:10,background:`${T.borderAccent}18`,color:T.borderAccent,fontWeight:600,flexShrink:0}}>
                            {divActiveBidCount}
                          </span>
                        )}
                      </div>
                      {isDivOpen&&divScopes.map(scope=>renderScopeNode(scope,22))}
                    </div>
                  );
                })}
                {/* Orphan scopes (no division assigned) */}
                {orphanScopes.length>0&&(
                  <>
                    <div style={{padding:"6px 8px 2px",fontSize:10,color:T.textWeak,letterSpacing:".1em",textTransform:"uppercase" as any,marginTop:4}}>Other Scopes</div>
                    {orphanScopes.map(scope=>renderScopeNode(scope,14))}
                  </>
                )}
                {bidScopes.length===0&&(
                  <div style={{padding:"20px 14px",fontSize:10,color:T.textMuted,fontStyle:"italic",textAlign:"center" as any}}>
                    No trade scopes yet.
                  </div>
                )}
              </>
            );
          })()}
        </div>
      </div>
    )}

    {/* Inline new scope form (shared between both views when visible) */}
    {showNewScope&&bidNavMode==="tree"&&(
      <div style={{padding:"8px 10px",borderTop:`1px solid ${T.borderPrimary}`,flexShrink:0}}>
        <div style={{fontSize:10,color:T.textWeak,marginBottom:4,letterSpacing:".08em",textTransform:"uppercase" as any}}>New Trade Scope</div>
        <input autoFocus placeholder="Scope name…" value={newScopeName} onChange={e=>setNewScopeName(e.target.value)}
          onKeyDown={e=>{if(e.key==="Escape"){setShowNewScope(false);setNewScopeName("");setNewScopeDivIds([]);setNewScopeSubIds([]);}}}
          style={{width:"100%",background:T.bgInput,border:`1px solid ${T.borderAccent}`,borderRadius:3,color:T.textPrimary,fontSize:11,padding:"4px 8px",fontFamily:FONT,boxSizing:"border-box" as any,marginBottom:6}}/>
        <ScopeWorkPicker T={T} divisions={divisions} selected={newScopeSubIds} setSelected={setNewScopeSubIds} maxHeight={150}/>
        <div style={{display:"flex",gap:4}}>
          <button onClick={()=>{
            const name=newScopeName.trim();
            if(!name)return;
            const newScope:BidScope={id:uid(),name,divisionIds:divisionsOf(divisions,newScopeSubIds),subIds:newScopeSubIds};
            setBidScopes(p=>[...p,newScope]);
            setActiveBidScope(newScope.id);
            // auto-expand the relevant division in tree mode
            newScopeDivIds.forEach(did=>setBidTreeExpanded(p=>p.includes(`div-${did}`)?p:[...p,`div-${did}`]));
            setBidTreeExpanded(p=>[...p,`scope-${newScope.id}`]);
            setShowNewScope(false);setNewScopeName("");setNewScopeDivIds([]);setNewScopeSubIds([]);
          }} style={{flex:1,fontSize:10,padding:"4px 0",background:T.borderAccent,color:T.textOnGold,border:"none",borderRadius:3,cursor:"pointer",fontFamily:FONT,fontWeight:700}}>
            + Add
          </button>
          <button onClick={()=>{setShowNewScope(false);setNewScopeName("");setNewScopeDivIds([]);setNewScopeSubIds([]);}}
            style={{flex:1,fontSize:10,padding:"4px 0",background:"none",color:T.textWeak,border:`1px solid ${T.borderSecondary}`,borderRadius:3,cursor:"pointer",fontFamily:FONT}}>Cancel</button>
        </div>
      </div>
    )}
    </div>{/* end collapsible sidebar contents */}
  </div>

  {/* ── Center: Content ── */}
  <div style={{flex:1,display:"flex",flexDirection:"column"as any,overflow:"hidden"}}>

    {/* ── Persistent top bar: breadcrumb + action buttons ── */}
    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:isMobile?"12px 16px":"16px 36px 0",flexShrink:0}}>
      <div style={{display:"flex",alignItems:"center",gap:8}}>
        <span style={{fontSize:20,fontWeight:700,color:T.borderAccent,fontFamily:FONT}}>
          {activeScope?activeScope.name:"Bids"}
        </span>
      </div>
      <div style={{display:"flex",gap:8,alignItems:"center",flexShrink:0,position:"relative"}}>
        {/* ── Create Document button — only shown on overview (scope detail has its own button) ── */}
        {!activeBidScope&&<div style={{position:"relative"}}>
          <button
            onClick={()=>{
              // No scope selected — show scope picker
              setShowDocScopePicker(v=>!v);
            }}
            style={{display:"flex",alignItems:"center",gap:7,padding:"9px 16px",fontSize:12,fontWeight:600,
              border:`1px solid ${T.borderAccent}`,borderRadius:6,
              background:showDocScopePicker?`${T.borderAccent}22`:"none",
              color:T.borderAccent,cursor:"pointer",fontFamily:FONT,whiteSpace:"nowrap" as any}}>
            <Icon.FileText size={13}/> Bid Form
            <span style={{fontSize:10,marginLeft:2,opacity:.7}}>▾</span>
          </button>
          {/* Scope picker dropdown */}
          {showDocScopePicker&&(
            <>
              <div style={{position:"fixed",inset:0,zIndex:1050}} onClick={()=>setShowDocScopePicker(false)}/>
              <div style={{
                position:"absolute",top:"calc(100% + 6px)",right:0,
                background:T.bgPanel,border:`1px solid ${T.borderPrimary}`,
                borderRadius:6,boxShadow:"0 8px 24px rgba(0,0,0,0.22)",
                zIndex:1051,minWidth:260,maxWidth:340,overflow:"hidden",
              }}>
                <div style={{padding:"10px 14px 6px",fontSize:10,color:T.textWeak,letterSpacing:".12em",textTransform:"uppercase" as any,borderBottom:`1px solid ${T.borderPrimary}`,fontFamily:FONT}}>
                  Select a trade scope to generate a document
                </div>
                <div style={{maxHeight:280,overflowY:"auto"}}>
                  {bidScopes.length===0?(
                    <div style={{padding:"16px 14px",fontSize:11,color:T.textMuted,fontStyle:"italic",fontFamily:FONT}}>
                      No trade scopes yet — create one in the left panel.
                    </div>
                  ):bidScopes.map(scope=>{
                    const divId=scope.divisionIds[0]||"div1";
                    const div=divisions.find(d=>d.id===divId);
                    const bidCount=bids.filter(b=>b.scopeId===scope.id&&b.status==="Active").length;
                    return(
                      <div key={scope.id}
                        onClick={()=>{
                          setShowSowEditor({divisionId:divId,divisionName:div?.name||scope.name,tradeName:scope.name,tradeContact:"",scopeId:scope.id});
                          setShowDocScopePicker(false);
                        }}
                        style={{display:"flex",alignItems:"center",gap:10,padding:"10px 14px",cursor:"pointer",borderBottom:`1px solid ${T.borderPrimary}22`,transition:"background 0.1s"}}
                        onMouseEnter={e=>e.currentTarget.style.background=T.bgHover}
                        onMouseLeave={e=>e.currentTarget.style.background=""}>
                        <Icon.FileText size={14} color={T.borderAccent}/>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontSize:12,fontWeight:600,color:T.textSecondary,fontFamily:FONT,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" as any}}>{scope.name}</div>
                          {div&&<div style={{fontSize:10,color:T.textMuted,fontFamily:FONT}}>{div.code} · {div.name.replace(/^Div(ision)? \d+ [–-] /,"")}</div>}
                        </div>
                        {bidCount>0&&<span style={{fontSize:10,padding:"1px 6px",borderRadius:10,background:`${T.borderAccent}18`,color:T.borderAccent,fontWeight:700,flexShrink:0}}>{bidCount} bid{bidCount!==1?"s":""}</span>}
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>}
        {/* The one primary action on this screen. Both ways of getting bids
            in live behind it: a cryptic second button beside it ("BC") was
            the same concept wearing a disguise, parked next to a button that
            creates scopes. */}
        <div style={{position:"relative"}}>
          <button onClick={()=>setShowImportMenu(v=>!v)}
            style={{display:"flex",alignItems:"center",gap:8,padding:"9px 20px",fontSize:13,fontWeight:700,
              border:"none",borderRadius:6,background:T.borderAccent,color:T.textOnGold,
              cursor:"pointer",fontFamily:FONT,boxShadow:`0 2px 8px ${T.borderAccent}33`}}>
            + Import Bid <span style={{fontSize:10,opacity:.8}}>&#9662;</span>
          </button>
          {showImportMenu&&(<>
            <div onClick={()=>setShowImportMenu(false)} style={{position:"fixed",inset:0,zIndex:40}}/>
            <div style={{position:"absolute",top:"calc(100% + 6px)",right:0,zIndex:41,minWidth:290,
              background:T.bgPanel,border:`1px solid ${T.borderPrimary}`,borderRadius:6,
              boxShadow:"0 8px 24px rgba(0,0,0,.5)",overflow:"hidden",fontFamily:FONT}}>
              {([
                {label:"A subcontractor's proposal",hint:"One bid, from a PDF or spreadsheet",
                 go:()=>{setShowBidImport(true);setBidImportScopeId(activeBidScope||bidScopes[0]?.id||null);}},
                {label:"A BuildingConnected package",hint:"Every bidder at once, with your plugs",
                 go:onImportBcLeveling},
              ]).map(o=>(
                <div key={o.label} onClick={()=>{setShowImportMenu(false);o.go();}}
                  style={{padding:"10px 14px",cursor:"pointer",borderBottom:`1px solid ${T.borderPrimary}`}}
                  onMouseEnter={e=>e.currentTarget.style.background=T.bgHover}
                  onMouseLeave={e=>e.currentTarget.style.background=""}>
                  <div style={{fontSize:12,color:T.textSecondary,fontWeight:600}}>{o.label}</div>
                  <div style={{fontSize:10,color:T.textWeak,marginTop:2}}>{o.hint}</div>
                </div>
              ))}
            </div>
          </>)}
        </div>
      </div>
    </div>

    {/* ── Scrollable content ── */}
    <div style={{flex:1,overflowY:"auto",padding:isMobile?"14px 16px":isTablet?"20px 24px":"20px 36px 36px"}}>

    {/* ── NO SCOPE SELECTED: Overview cards ── */}
    {!activeBidScope&&(
      <div>
        {/* Sub-header */}
        <div style={{marginBottom:20}}>
          <div style={{fontSize:11,color:T.textWeak}}>Select a scope to compare bids, or import a new proposal to get started.</div>
        </div>

        {/* KPI cards */}
        <div style={{display:"flex",gap:10,marginBottom:16,flexWrap:"wrap" as any}}>
          {[
            {label:"Scopes",value:String(bidScopes.length),col:T.borderAccent},
            {label:"Active Bids",value:String(bids.filter(b=>b.status==="Active").length),col:T.positive},
            {label:"Partners Bidding",value:String(new Set(bids.map(b=>b.partnerId)).size),col:"#d4a76a"},
            {label:"Registered",value:String(bidPartners.length),col:T.textSecondary},
          ].map(({label,value,col})=>(
            <div key={label} style={{flex:1,minWidth:80,background:T.bgPanel,border:`1px solid ${T.borderPrimary}`,borderRadius:6,padding:"12px 14px"}}>
              <div style={{fontSize:10,color:T.textWeak,letterSpacing:".14em",textTransform:"uppercase"}}>{label}</div>
              <div style={{fontSize:20,fontWeight:700,color:col}}>{value}</div>
            </div>
          ))}
        </div>

        {/* ── Buyout CTA banner ── */}
        <div
          onClick={()=>setActiveTab("buyout")}
          style={{
            display:"flex",alignItems:"center",justifyContent:"space-between",
            background:`${T.borderAccent}12`,
            border:`1px solid ${T.borderAccent}44`,
            borderRadius:6,padding:"12px 18px",marginBottom:20,
            cursor:"pointer",transition:"background 0.15s",
          }}
          onMouseEnter={e=>(e.currentTarget.style.background=`${T.borderAccent}22`)}
          onMouseLeave={e=>(e.currentTarget.style.background=`${T.borderAccent}12`)}
        >
          <div>
            <div style={{fontSize:12,fontWeight:700,color:T.borderAccent,fontFamily:FONT}}>Ready to award a contract?</div>
            <div style={{fontSize:11,color:T.textWeak,marginTop:2,fontFamily:FONT}}>Track awarded scopes, contracts, and buyout savings in the Buyout Log.</div>
          </div>
          <div style={{fontSize:13,fontWeight:700,color:T.borderAccent,fontFamily:FONT,flexShrink:0,marginLeft:16}}>Buyout Log →</div>
        </div>

        {/* ── Bid Timeline Chart ── */}
        {(()=>{
          const datedBids=bids.filter(b=>b.date&&bidTotal(b)>0);
          if(datedBids.length<1)return null;
          const CW=bidChartW,CH=200;
          const PL=70,PR=16,PT=30,PB=42;
          const pW=CW-PL-PR,pH=CH-PT-PB;
          const toTs=(d:string)=>new Date(d+"T00:00:00").getTime();

          // All snapshot anchors, sorted chronologically
          const allSnaps=(projectSnapshots||[])
            .filter(s=>s.date||s.createdAt)
            .map(s=>({id:s.id,label:s.label||"Snapshot",date:(s.date||s.createdAt).slice(0,10),ts:toTs((s.date||s.createdAt).slice(0,10))}))
            .sort((a,b)=>a.ts-b.ts);

          const allBidTs=datedBids.map(b=>toTs(b.date));
          const latestBidTs=allBidTs.length?Math.max(...allBidTs):Date.now();
          const allSnapTs=allSnaps.map(s=>s.ts);

          // Compute visible window based on range mode
          let visMinTs:number,visMaxTs:number;
          if(bidChartRangeMode==="iteration"){
            // Most recent snapshot → latest bid date
            const lastSnap=allSnapTs.length?Math.max(...allSnapTs):Math.min(...allBidTs);
            visMinTs=lastSnap;
            visMaxTs=latestBidTs;
          } else if(bidChartRangeMode==="custom"){
            visMinTs=bidChartCustomStart?toTs(bidChartCustomStart):Math.min(...allBidTs,...allSnapTs);
            visMaxTs=bidChartCustomEnd?toTs(bidChartCustomEnd):latestBidTs;
          } else {
            // "all" — everything
            visMinTs=Math.min(...allBidTs,...(allSnapTs.length?allSnapTs:[latestBidTs]));
            visMaxTs=Math.max(...allBidTs,...(allSnapTs.length?allSnapTs:[latestBidTs]));
          }
          if(visMaxTs<=visMinTs)visMaxTs=visMinTs+86400000*30;

          // Add 5% breathing room on each side
          const rawSpan=visMaxTs-visMinTs;
          const pad=rawSpan*0.05||86400000;
          const minTs=visMinTs-pad,maxTs=visMaxTs+pad;
          const tsRange=maxTs-minTs;
          const tsX=(ts:number)=>PL+((ts-minTs)/tsRange)*pW;

          // Only show bids within the visible window
          const visibleBids=datedBids.filter(b=>{const t=toTs(b.date);return t>=minTs&&t<=maxTs;});

          const amounts=visibleBids.map(b=>bidTotal(b));
          const rawMax=amounts.length?Math.max(...amounts):1;
          const rawMin=amounts.length?Math.min(...amounts):0;
          // Snap to nice round units so ticks are always clean numbers
          const snapUnit=rawMax>2000000?500000:rawMax>1000000?250000:rawMax>500000?100000:rawMax>200000?50000:rawMax>50000?25000:10000;
          const yFloor=Math.max(0,Math.floor(rawMin*0.85/snapUnit)*snapUnit);
          const maxAmt=Math.ceil(rawMax*1.1/snapUnit)*snapUnit;
          const amtY=(a:number)=>PT+pH-((a-yFloor)/(maxAmt-yFloor))*pH;

          // Y axis ticks — evenly spaced from floor to top
          const yTicks=Array.from({length:5},(_,i)=>{
            const a=yFloor+(maxAmt-yFloor)/4*i;
            const lbl=a>=1000000?`$${(a/1000000).toFixed(1)}M`:a>=1000?`$${Math.round(a/1000)}k`:`$${Math.round(a)}`;
            return{a,lbl};
          });

          // Auto x-axis granularity based on visible span
          const spanDays=rawSpan/86400000;
          const autoScale=spanDays<90?"week":spanDays<600?"month":spanDays<1095?"quarter":"year";
          const xTicks:{ts:number;lbl:string}[]=[];
          {
            const cur=new Date(minTs);
            if(autoScale==="week"){
              cur.setDate(cur.getDate()-cur.getDay());
              while(cur.getTime()<=maxTs+86400000*7){
                xTicks.push({ts:cur.getTime(),lbl:cur.toLocaleDateString("en-US",{month:"short",day:"numeric"})});
                cur.setDate(cur.getDate()+7);
              }
            } else if(autoScale==="month"){
              cur.setDate(1);
              while(cur.getTime()<=maxTs){
                xTicks.push({ts:cur.getTime(),lbl:cur.toLocaleDateString("en-US",{month:"short",year:"2-digit"})});
                cur.setMonth(cur.getMonth()+1);
              }
            } else if(autoScale==="quarter"){
              cur.setDate(1);cur.setMonth(Math.floor(cur.getMonth()/3)*3);
              while(cur.getTime()<=maxTs){
                const q=Math.floor(cur.getMonth()/3)+1;
                xTicks.push({ts:cur.getTime(),lbl:`Q${q} '${String(cur.getFullYear()).slice(2)}`});
                cur.setMonth(cur.getMonth()+3);
              }
            } else {
              cur.setMonth(0);cur.setDate(1);
              while(cur.getTime()<=maxTs){
                xTicks.push({ts:cur.getTime(),lbl:String(cur.getFullYear())});
                cur.setFullYear(cur.getFullYear()+1);
              }
            }
          }

          // Snapshot milestone lines — only those within visible window
          const snapLines=allSnaps.filter(s=>s.ts>=minTs&&s.ts<=maxTs).map(s=>({id:s.id,lbl:s.label.slice(0,18),ts:s.ts}));

          // Options for custom range selects
          const rangeOptFmt=(d:string)=>new Date(d+"T00:00:00").toLocaleDateString("en-US",{month:"short",year:"numeric"});

          return(
            <div style={{marginTop:20,background:T.bgPanel,border:`1px solid ${T.borderPrimary}`,borderRadius:6,padding:"12px 14px 10px"}}>
              {/* ── Timeline controls row ── */}
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10,flexWrap:"wrap" as any,gap:8}}>
                {/* Range mode styled select */}
                <div style={{display:"flex",alignItems:"center",gap:8}}>
                  <span style={{fontSize:10,fontWeight:700,color:T.textWeak,letterSpacing:".1em",textTransform:"uppercase" as any}}>Bid Timeline</span>
                  <select value={bidChartRangeMode} onChange={e=>setBidChartRangeMode(e.target.value as any)}
                    style={{fontSize:11,padding:"4px 24px 4px 10px",borderRadius:3,cursor:"pointer",fontFamily:FONT,fontWeight:600,outline:"none",
                      background:T.bgInput,color:T.borderAccent,
                      border:`1px solid ${T.borderAccent}55`,
                      appearance:"none" as any,WebkitAppearance:"none" as any,
                      backgroundImage:"url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='8' height='5' viewBox='0 0 8 5'%3E%3Cpath d='M0 0l4 5 4-5z' fill='%23c8a84a' opacity='0.7'/%3E%3C/svg%3E\")",
                      backgroundRepeat:"no-repeat",backgroundPosition:"right 7px center"}}>
                    <option value="all">All Time</option>
                    <option value="iteration" disabled={allSnaps.length===0}>Since Last Milestone</option>
                    <option value="custom">Custom Range</option>
                  </select>
                </div>
                {/* Right side: custom range pickers + scope filter */}
                <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap" as any}}>
                  {bidChartRangeMode==="custom"&&(
                    <div style={{display:"flex",gap:4,alignItems:"center"}}>
                      <select value={bidChartCustomStart} onChange={e=>setBidChartCustomStart(e.target.value)}
                        className="input-field" style={{fontSize:11,padding:"3px 7px",cursor:"pointer"}}>
                        <option value="">Earliest</option>
                        {allSnaps.map(s=>(
                          <option key={s.id} value={s.date}>{s.label} ({rangeOptFmt(s.date)})</option>
                        ))}
                      </select>
                      <span style={{fontSize:11,color:T.textWeak}}><Icon.ArrowRight size={11}/> </span>
                      <select value={bidChartCustomEnd} onChange={e=>setBidChartCustomEnd(e.target.value)}
                        className="input-field" style={{fontSize:11,padding:"3px 7px",cursor:"pointer"}}>
                        <option value="">Current</option>
                        {allSnaps.map(s=>(
                          <option key={s.id} value={s.date}>{s.label} ({rangeOptFmt(s.date)})</option>
                        ))}
                      </select>
                    </div>
                  )}
                  {/* Scope filter pills — also serve as legend; wrap for unlimited scopes */}
                  {bidScopes.some(scope=>datedBids.some(b=>b.scopeId===scope.id))&&(
                    <div style={{display:"flex",gap:4,alignItems:"center",flexWrap:"wrap" as any}}>
                      {bidScopes.map((scope,i)=>{
                        if(!datedBids.some(b=>b.scopeId===scope.id))return null;
                        const active=bidChartScopeFilter===scope.id;
                        const col=BCOLORS[i%BCOLORS.length];
                        return(
                          <button key={scope.id} onClick={()=>setBidChartScopeFilter(active?null:scope.id)}
                            style={{fontSize:10,padding:"3px 9px 3px 7px",borderRadius:3,cursor:"pointer",fontFamily:FONT,fontWeight:600,transition:"all .15s",
                              display:"flex",alignItems:"center",gap:5,
                              background:active?`${col}18`:"transparent",
                              color:active?col:T.textWeak,
                              border:`1px solid ${active?col+"88":T.borderSecondary}`}}>
                            <span style={{width:7,height:7,borderRadius:"50%",background:col,opacity:active?1:.55,display:"inline-block",flexShrink:0}}/>
                            {scope.name}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
              <div ref={(el)=>{if(el){const w=el.getBoundingClientRect().width;if(w>0&&Math.abs(w-bidChartW)>4)setBidChartW(Math.floor(w));}}} style={{width:"100%"}}>
              <svg width={CW} height={CH} style={{overflow:"visible" as any,display:"block"}}>
                {/* Y grid + labels */}
                {yTicks.map(tick=>(
                  <g key={tick.a}>
                    <line x1={PL} y1={amtY(tick.a)} x2={PL+pW} y2={amtY(tick.a)} stroke={T.borderPrimary} strokeWidth="1" strokeDasharray="3,5" opacity=".4"/>
                    <text x={PL-7} y={amtY(tick.a)+4} textAnchor="end" fontSize="13" fill={T.textMuted} fontFamily={FONT}>{tick.lbl}</text>
                  </g>
                ))}
                {/* X axis */}
                <line x1={PL} y1={PT+pH} x2={PL+pW} y2={PT+pH} stroke={T.borderSecondary} strokeWidth="1"/>
                {xTicks.map(tick=>(
                  <g key={tick.ts}>
                    <line x1={tsX(tick.ts)} y1={PT+pH} x2={tsX(tick.ts)} y2={PT+pH+5} stroke={T.borderSecondary} strokeWidth="1"/>
                    <text x={tsX(tick.ts)} y={PT+pH+18} textAnchor="middle" fontSize="12" fill={T.textMuted} fontFamily={FONT}>{tick.lbl}</text>
                  </g>
                ))}
                {/* Snapshot milestone lines — bright solid verticals, clickable */}
                {snapLines.map((snap,si)=>{
                  const x=tsX(snap.ts);
                  const lblW=snap.lbl.length*7.2+16;
                  // Label pill sits ABOVE the chart area, line starts at PT — no overlap
                  return(
                    <g key={si} style={{cursor:"pointer"}} onClick={()=>{
                      setBidSnapModalId(snap.id);
                      setAnalysisLeft(snap.id);
                      setAnalysisRight("current");
                    }}>
                      <rect x={x-lblW/2} y={2} width={lblW} height={20} rx={3} fill="#e2c96e33" stroke="#e2c96e" strokeWidth="1.5"/>
                      <text x={x} y={16} textAnchor="middle" fontSize="11" fill="#e2c96e" fontFamily={FONT} fontWeight={700}>{snap.lbl}</text>
                      <line x1={x} y1={PT} x2={x} y2={PT+pH} stroke="#e2c96e" strokeWidth="1.5" strokeDasharray="4,3" opacity=".85"/>
                    </g>
                  );
                })}
                {/* Bid dots — only within visible window */}
                {visibleBids.map(bid=>{
                  const scopeIdx=bidScopes.findIndex(s=>s.id===bid.scopeId);
                  const color=BCOLORS[scopeIdx%BCOLORS.length];
                  const ts=toTs(bid.date);
                  const amt=bidTotal(bid);
                  const cx=tsX(ts),cy=amtY(amt);
                  const isHov=bidChartHover?.bid.id===bid.id;
                  const faded=!!(bidChartScopeFilter&&bid.scopeId!==bidChartScopeFilter);
                  const superseded=bid.status==="Superseded"||bid.status==="Rejected";
                  return(
                    <g key={bid.id}
                      onMouseEnter={()=>setBidChartHover({bid,x:cx,y:cy})}
                      onMouseLeave={()=>setBidChartHover(null)}
                      onClick={()=>setActiveBidScope(bid.scopeId)}
                      style={{cursor:"pointer",opacity:faded?.08:superseded?.4:1,transition:"opacity .2s"}}>
                      <circle cx={cx} cy={cy} r={isHov?6:4}
                        fill={color} fillOpacity={superseded?.45:.88}
                        stroke={isHov?"#fff":color} strokeWidth={isHov?1.5:1}/>
                      {superseded&&<circle cx={cx} cy={cy} r={isHov?6:4} fill="none" stroke={color} strokeWidth="1" strokeDasharray="2,2" opacity=".5"/>}
                    </g>
                  );
                })}
                {/* Tooltip */}
                {bidChartHover&&(()=>{
                  const{bid,x:cx,y:cy}=bidChartHover;
                  const partner=bidPartners.find(p=>p.id===bid.partnerId);
                  const scope=bidScopes.find(s=>s.id===bid.scopeId);
                  const amt=bidTotal(bid);
                  const dateStr=bid.date?new Date(bid.date+"T00:00:00").toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"}):"";
                  const lines=[
                    {text:partner?.name||"Unknown",bold:true,color:T.textPrimary},
                    {text:scope?.name||"",bold:false,color:T.textWeak},
                    {text:fmt(amt),bold:true,color:T.borderAccent},
                    {text:dateStr,bold:false,color:T.textMuted},
                    ...(bid.status!=="Active"?[{text:bid.status,bold:false,color:T.negative}]:[]),
                  ].filter(l=>l.text);
                  const ttW=170,ttH=lines.length*16+12;
                  const ttX=cx+12+ttW>CW?cx-ttW-12:cx+12;
                  const ttY=Math.max(PT,Math.min(cy-ttH/2,PT+pH-ttH));
                  return(
                    <g style={{pointerEvents:"none" as any}}>
                      <rect x={ttX} y={ttY} width={ttW} height={ttH} rx="3"
                        fill={T.bgPanel} stroke={T.borderPrimary} strokeWidth="1"
                        filter="drop-shadow(0 3px 10px rgba(0,0,0,.45))"/>
                      {lines.map((l,i)=>(
                        <text key={i} x={ttX+10} y={ttY+16+i*16}
                          fontSize="11" fill={l.color} fontFamily={FONT} fontWeight={l.bold?"700":"400"}>
                          {l.text}
                        </text>
                      ))}
                    </g>
                  );
                })()}
              </svg>
              </div>
              <div style={{fontSize:10,color:T.textMuted,marginTop:4,opacity:.7}}>
                Click a dot to open that scope · click a milestone label to view snapshots · faded = superseded · click a scope pill to filter
                {bidChartRangeMode!=="all"&&visibleBids.length<datedBids.length&&(
                  <span style={{marginLeft:8,color:T.borderAccent}}>
                    · {datedBids.length-visibleBids.length} bid{datedBids.length-visibleBids.length!==1?"s":""} outside range hidden
                  </span>
                )}
              </div>
            </div>
          );
        })()}

      </div>
    )}

    {/* ── SCOPE SELECTED ── */}
    {activeBidScope&&activeScope&&activeScopeStat&&(()=>{
      const{activeBids,scopeBids}=activeScopeStat;

      // ── Compare: build items×partners grid ──
      const partnerIds=[...new Set(activeBids.map(b=>b.partnerId))];
      const allLineItems:Map<string,{desc:string;partnerTotals:Record<string,number>}>=new Map();
      activeBids.forEach(b=>{
        (b.items||[]).forEach(bi=>{
          const key=bi.description.toLowerCase().trim();
          if(!allLineItems.has(key))allLineItems.set(key,{desc:bi.description,partnerTotals:{}});
          const entry=allLineItems.get(key)!;
          entry.partnerTotals[b.partnerId]=(entry.partnerTotals[b.partnerId]||0)+(bi.qty||1)*(bi.unitCost||0);
        });
      });
      const lineItemRows=[...allLineItems.values()];
      // Column totals per partner
      const partnerColumnTotals:Record<string,number>={};
      partnerIds.forEach(pid=>{partnerColumnTotals[pid]=activeBids.filter(b=>b.partnerId===pid).reduce((s,b)=>s+bidTotal(b),0);});
      const lowestPartnerTotal=partnerIds.length?Math.min(...partnerIds.map(pid=>partnerColumnTotals[pid])):0;

      // ── Estimate baseline ──
      // Narrowed scopes budget their own subcategories; the rest still sum
      // whole divisions, which is what every scope did before subIds existed.
      const estimateScopeTotal=scopeBudget(activeScope,divisions,itemState);
      // Estimate line items for this scope (for the detail list)
      // Same source as the total above it, so the list and the figure can
      // never disagree about what is in the package.
      const estimateLineItems:(()=>{desc:string,divName:string,amt:number,itemId:string,divId:string}[])=()=>
        scopeLineItems(activeScope,divisions,itemState)
          .map(r=>({desc:r.desc,divName:r.divName,amt:r.amt,itemId:r.itemId,divId:r.divId}));

      // The same computation the sidebar uses for its Awarded badge, for the
      // package actually on screen.
      const scopePipeline=computePackagePipeline(activeScope,bids,levelingAdjustments,buyoutItems||[]);

      // ── History: version cards per partner ──
      const historyByPartner:Record<string,Bid[]>={};
      scopeBids.forEach(b=>{(historyByPartner[b.partnerId]=historyByPartner[b.partnerId]||[]).push(b);});
      Object.values(historyByPartner).forEach(arr=>arr.sort((a,b)=>b.version-a.version));

      return(
        <div>
          {/* What stage this package is at, and the one thing to do next.
              computePackagePipeline has produced `nextAction` on every render
              since it was written and nothing has ever displayed it, so every
              package looked identical whether it had no bids or three and was
              ready to award. Controls that cannot act yet are not drawn:
              there is nothing to award before anyone has bid. */}
          <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:16,flexWrap:"wrap" as any}}>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:11,color:T.textWeak}}>
                {activeBids.length} active bid{activeBids.length!==1?"s":""} · {scopeBids.length} total
              </div>
              <div style={{fontSize:11,marginTop:2,fontWeight:600,
                color:scopePipeline.awarded?T.positive
                  :scopePipeline.stage==="level"?T.borderAccent
                  :T.costStatus["Needs Review"].badgeText}}>
                {scopePipeline.nextAction}
              </div>
            </div>
            {/* Award & Track in Buyout button */}
            {scopePipeline.bidCount>0&&<button
              onClick={()=>setActiveTab("buyout")}
              style={{display:"flex",alignItems:"center",gap:7,
                background:`${T.borderAccent}14`,border:`1px solid ${T.borderAccent}55`,
                color:T.borderAccent,cursor:"pointer",
                padding:"5px 13px",minHeight:24,borderRadius:3,
                fontFamily:FONT,fontSize:11,fontWeight:600,flexShrink:0}}
              title="Formalize the awarded contract in the Buyout Log">
              Award &amp; Track in Buyout →
            </button>}
            {/* These sheets get read in a room, off paper, by people who
                will act on them -- so the print lives where the comparison
                does, not buried in a File menu. */}
            {scopePipeline.bidCount>0&&<button
              onClick={onPrintComparison}
              title="Print or save this bid comparison"
              style={{display:"flex",alignItems:"center",gap:7,
                background:"none",border:`1px solid ${T.borderSecondary}`,
                color:T.textWeak,cursor:"pointer",
                padding:"5px 13px",minHeight:24,borderRadius:3,
                fontFamily:FONT,fontSize:11,flexShrink:0}}>
              <Icon.Printer size={12}/> Print
            </button>}
            {/* Scope of Work / Bid Form button */}
            <button
              onClick={()=>{
                const divId=activeScope.divisionIds[0]||"div1";
                const div=divisions.find(d=>d.id===divId);
                setShowSowEditor({
                  divisionId:divId,
                  divisionName:div?.name||activeScope.name,
                  tradeName:activeScope.name,
                  tradeContact:"",
                  scopeId:activeScope.id,
                });
              }}
              style={{display:"flex",alignItems:"center",gap:7,
                background:"none",border:`1px solid ${T.borderSecondary}`,
                color:T.textWeak,cursor:"pointer",
                padding:"5px 13px",minHeight:24,borderRadius:3,
                fontFamily:FONT,fontSize:11,fontWeight:400,flexShrink:0}}
              title="Generate a Scope of Work or Bid Form for this trade scope">
              <Icon.FileText size={12}/> Scope of Work / Bid Form
            </button>
          </div>

          {/* Sub-tabs — segmented control */}
          {/* A view toggle changes nothing, so it does not get the weight of
              something that does. Same quiet treatment as the Estimate tab's
              By Division / Continuous switch. */}
          <div style={{display:scopeBids.length>0?"inline-flex":"none",gap:4,marginBottom:24,alignSelf:"flex-start"}}>
            {([
              {id:"compare" as const,icon:<Icon.Grid size={12}/>,label:"Compare Bids"},
              {id:"history" as const,icon:<Icon.RefreshCcw size={12}/>,label:"Bid History"},
            ]).map(({id,icon,label})=>{
              const isActive=bidSubTab===id;
              return(
                <button key={id} onClick={()=>setBidSubTab(id)}
                  style={{display:"flex",alignItems:"center",gap:6,padding:"4px 11px",minHeight:24,fontSize:10,fontWeight:isActive?600:400,
                    borderRadius:3,cursor:"pointer",fontFamily:FONT,
                    border:`1px solid ${isActive?T.borderAccent:T.borderSecondary}`,
                    background:isActive?T.bgAccentLight:"transparent",
                    color:isActive?T.borderAccent:T.textWeak,
                    transition:"all .15s",whiteSpace:"nowrap" as any}}>
                  <span style={{fontSize:11,lineHeight:1}}>{icon}</span>
                  {label}
                </button>
              );
            })}
          </div>

          {/* ── COMPARE SUB-TAB ── */}
          {bidSubTab==="compare"&&(()=>{
            const scopeId=activeBidScope!;
            const scopeAlignments=bidAlignments[scopeId]||{};
            const scopeHidden=hiddenPartners[scopeId]||[];
            const visiblePartnerIds=partnerIds.filter(pid=>!scopeHidden.includes(pid));

            // Estimate items with itemId
            const estItems=estimateLineItems();

            // Aligned rows: each estimate item + partner data aligned to it
            const alignedRows=estItems.map(ei=>{
              const partnerAmounts:Record<string,{total:number;bidItemIds:string[];bidId:string}>={};
              activeBids.forEach(bid=>{
                (bid.items||[]).forEach(bi=>{
                  if(scopeAlignments[bi.id]===ei.itemId){
                    if(!partnerAmounts[bid.partnerId])partnerAmounts[bid.partnerId]={total:0,bidItemIds:[],bidId:bid.id};
                    partnerAmounts[bid.partnerId].total+=(bi.qty||1)*(bi.unitCost||0);
                    partnerAmounts[bid.partnerId].bidItemIds.push(bi.id);
                  }
                });
              });
              return{...ei,partnerAmounts};
            });

            // Unassigned bid items grouped by description
            const unassignedMap=new Map<string,{desc:string;partnerTotals:Record<string,number>;bidItemIds:string[]}>();
            activeBids.forEach(bid=>{
              (bid.items||[]).forEach(bi=>{
                if(!scopeAlignments[bi.id]){
                  const key=bi.description.toLowerCase().trim();
                  if(!unassignedMap.has(key))unassignedMap.set(key,{desc:bi.description,partnerTotals:{},bidItemIds:[]});
                  const entry=unassignedMap.get(key)!;
                  entry.partnerTotals[bid.partnerId]=(entry.partnerTotals[bid.partnerId]||0)+(bi.qty||1)*(bi.unitCost||0);
                  entry.bidItemIds.push(bi.id);
                }
              });
            });
            const unassignedRows=[...unassignedMap.values()];

            // Column totals
            const partnerColumnTotals:Record<string,number>={};
            partnerIds.forEach(pid=>{partnerColumnTotals[pid]=activeBids.filter(b=>b.partnerId===pid).reduce((s,b)=>s+bidTotal(b),0);});
            const lowestVisibleTotal=visiblePartnerIds.length?Math.min(...visiblePartnerIds.map(pid=>partnerColumnTotals[pid]).filter(v=>v>0)):0;

            // ── Leveling adjustments for this scope ──
            const scopeLevelingAdjs=levelingAdjustments.filter(a=>a.scopeId===scopeId);
            // Leveled total = bid total + sum of amounts for non-included items
            const getLeveledTotal=(pid:string)=>{
              const base=partnerColumnTotals[pid]||0;
              const adj=scopeLevelingAdjs.reduce((sum,a)=>{
                const p=a.partners[pid];
                return sum+(p&&!p.included?(p.amount||0):0);
              },0);
              return base+adj;
            };
            const getTotalAdj=(pid:string)=>scopeLevelingAdjs.reduce((sum,a)=>{
              const p=a.partners[pid];
              return sum+(p&&!p.included?(p.amount||0):0);
            },0);
            const leveledTotals:Record<string,number>={};
            visiblePartnerIds.forEach(pid=>{leveledTotals[pid]=getLeveledTotal(pid);});
            const lowestLeveledTotal=visiblePartnerIds.length?Math.min(...visiblePartnerIds.map(pid=>leveledTotals[pid]).filter(v=>v>0)):0;

            // PDF helpers
            const partnerBidId:Record<string,string>={};
            partnerIds.forEach(pid=>{const ab=activeBids.find(b=>b.partnerId===pid);if(ab)partnerBidId[pid]=ab.id;});
            const bidsWithPdf=partnerIds.filter(pid=>{const b=activeBids.find(x=>x.partnerId===pid);return b&&(b.pdfUrl||b.pdfStoragePath||bidPdfCache.current.has(b.id));});

            // Actions
            const doAlign=(bidItemIds:string[],estimateItemId:string)=>{
              snapshot();
              setBidAlignments(prev=>{
                const next={...(prev[scopeId]||{})};
                // Overwrites any existing alignment for these ids — enables re-alignment by drag
                bidItemIds.forEach(id=>{next[id]=estimateItemId;});
                return{...prev,[scopeId]:next};
              });
              setDropTargetEstItemId(null);setDragBidItemIds(null);
            };
            const doUnalign=(bidItemIds:string[])=>{
              snapshot();
              setBidAlignments(prev=>{
                const next={...(prev[scopeId]||{})};
                bidItemIds.forEach(id=>{delete next[id];});
                return{...prev,[scopeId]:next};
              });
            };
            const doAddToEstimate=(row:{desc:string;partnerTotals:Record<string,number>;bidItemIds:string[]})=>{
              snapshot();
              const amounts=Object.values(row.partnerTotals).filter(v=>v>0);
              const lowest=amounts.length?Math.min(...amounts):0;
              const newItemId=uid();
              const firstDivId=activeScope.divisionIds[0];
              const firstDiv=divisions.find(d=>d.id===firstDivId);
              if(!firstDiv||!firstDiv.subcategories[0]){showToast("No division configured for this scope");return;}
              setDivisionsRaw(prev=>prev.map(div=>{
                if(div.id!==firstDivId)return div;
                return{...div,subcategories:div.subcategories.map((sub,si)=>{
                  if(si!==0)return sub;
                  return{...sub,rows:[...sub.rows,{type:"item",id:newItemId,description:row.desc,qty:1,unit:"LS",unitCost:lowest,proposal:"p1",notes:""}]};
                })};
              }));
              setItemState(prev=>({...prev,[newItemId]:{description:row.desc,qty:1,unit:"LS",unitCost:lowest,included:true,costStatus:"Hard Bid",proposal:"p1",notes:""}}));
              setBidAlignments(prev=>{
                const next={...(prev[scopeId]||{})};
                row.bidItemIds.forEach(id=>{next[id]=newItemId;});
                return{...prev,[scopeId]:next};
              });
              showToast(`"${row.desc.slice(0,32)}" added to ${firstDiv.name}`);
            };
            const doAccept=(estimateItemId:string,partnerId:string,data:{total:number;bidItemIds:string[];bidId:string})=>{
              snapshot();
              const partner=bidPartners.find(p=>p.id===partnerId);
              setItemState(prev=>({...prev,[estimateItemId]:{...(prev[estimateItemId]||{}),unitCost:data.total,qty:1,sourceBidId:data.bidId}}));
              showToast(`Accepted ${partner?.name||"partner"} price — undo to revert`);
            };
            const doAcceptAll=(partnerId:string)=>{
              const partner=bidPartners.find(p=>p.id===partnerId);
              snapshot();
              const updates:Record<string,any>={};
              alignedRows.forEach(row=>{
                const data=row.partnerAmounts[partnerId];
                if(data&&data.total>0)updates[row.itemId]={...(itemState[row.itemId]||{}),unitCost:data.total,qty:1,sourceBidId:data.bidId};
              });
              setItemState(prev=>({...prev,...updates}));
              showToast(`Accepted all bids from ${partner?.name||"partner"} — undo to revert`);
            };
            // ── Bid spread analysis (across visible partners' leveled totals) ──
            // Surfaces range, median, coefficient of variation, and outlier
            // partners (>20% off median) so PMs can spot anomalies before award.
            const spreadVals=visiblePartnerIds.map(pid=>leveledTotals[pid]).filter(v=>v>0).sort((a,b)=>a-b);
            let spread:null|{lo:number;hi:number;median:number;cv:number;range:number;outliers:{pid:string;total:number;deltaPct:number}[]}=null;
            if(spreadVals.length>=2){
              const lo=spreadVals[0],hi=spreadVals[spreadVals.length-1];
              const mid=spreadVals.length%2?spreadVals[(spreadVals.length-1)/2]:(spreadVals[spreadVals.length/2-1]+spreadVals[spreadVals.length/2])/2;
              const mean=spreadVals.reduce((s,v)=>s+v,0)/spreadVals.length;
              const variance=spreadVals.reduce((s,v)=>s+(v-mean)**2,0)/spreadVals.length;
              const cv=mean>0?Math.sqrt(variance)/mean:0;
              const outliers=visiblePartnerIds.map(pid=>{
                const t=leveledTotals[pid];
                if(!t)return null;
                const deltaPct=mid>0?((t-mid)/mid):0;
                return Math.abs(deltaPct)>=0.20?{pid,total:t,deltaPct}:null;
              }).filter(Boolean) as {pid:string;total:number;deltaPct:number}[];
              spread={lo,hi,median:mid,cv,range:hi-lo,outliers};
            }

            // ── Plug vs. bid: the number that matters during bid week ──
            const pvb=computePlugVsBid(estimateScopeTotal,visiblePartnerIds.map(pid=>leveledTotals[pid]||0));
            const pvbUnder=pvb.delta<=0;
            const pvbTone=pvbUnder?T.positive:T.negative;
            return(
              <div>
                {/* ── One summary band ──────────────────────────────────
                    This was two: a boxed three-cell strip (budget / low bid /
                    delta) and, directly beneath it, a flat inline row of five
                    spread statistics. The same subject in two visual
                    languages, stacked, pushing the comparison table to 69% of
                    the way down the viewport.

                    Now one row: the three figures that decide anything, the
                    spread as supporting detail, and outliers where they are
                    already looked at. */}
                {pvb.hasBids&&(
                  <div style={{marginBottom:14,background:T.bgPanel,border:`1px solid ${T.borderPrimary}`,borderRadius:6,overflow:"hidden"}}>
                    <div style={{display:"flex",alignItems:"stretch",flexWrap:"wrap" as any}}>
                      {pvb.budget>0&&(
                        <div style={{flex:"1 1 180px",padding:"9px 16px",borderRight:`1px solid ${T.borderPrimary}`}}>
                          <div style={{fontSize:10,color:T.textWeak,letterSpacing:".1em",textTransform:"uppercase" as any,fontWeight:700,marginBottom:2}}>Estimate (Budget)</div>
                          <div style={{fontSize:16,fontWeight:700,color:T.textPrimary,fontVariantNumeric:"tabular-nums"}}>{fmt(pvb.budget)}</div>
                          {/* A budget is only comparable to a bid if both
                              cover the same work, and a scope left on whole
                              divisions usually does not. */}
                          <div style={{fontSize:10,color:isNarrowed(activeScope)?T.textWeak:T.costStatus["Needs Review"].badgeText,marginTop:2}}>
                            {scopeCoverage(activeScope,divisions)}
                            {!isNarrowed(activeScope)&&<span title="This scope budgets whole divisions, which is usually more work than one package covers."> &#9888;</span>}
                          </div>
                        </div>
                      )}
                      <div style={{flex:"1 1 160px",padding:"9px 16px",borderRight:`1px solid ${T.borderPrimary}`}}>
                        <div style={{fontSize:10,color:T.textWeak,letterSpacing:".1em",textTransform:"uppercase" as any,fontWeight:700,marginBottom:2}}>Low Leveled Bid</div>
                        <div style={{fontSize:16,fontWeight:700,color:T.textPrimary,fontVariantNumeric:"tabular-nums"}}>{fmt(pvb.lowBid)}</div>
                        {spread&&<div style={{fontSize:10,color:T.textWeak,marginTop:2}}>
                          of {fmt(spread.lo)}&ndash;{fmt(spread.hi)} across {visiblePartnerIds.length}
                        </div>}
                      </div>
                      {pvb.budget>0&&(
                        <div style={{flex:"1 1 180px",padding:"9px 16px",background:`${pvbTone}10`,borderRight:spread?`1px solid ${T.borderPrimary}`:undefined}}>
                          <div style={{fontSize:10,color:pvbTone,letterSpacing:".1em",textTransform:"uppercase" as any,fontWeight:700,marginBottom:2}}>
                            {pvbUnder?"Under Budget":"Over Budget"}
                          </div>
                          {/* The percentage no longer wears its own brackets.
                              "($91,050)(-19.8%)" was two bracketed numbers
                              jammed together, because negatives already print
                              in parentheses in this product. */}
                          <div style={{fontSize:16,fontWeight:700,color:pvbTone,fontVariantNumeric:"tabular-nums"}}>
                            {fmt(Math.abs(pvb.delta))}
                            <span style={{fontSize:11,fontWeight:600,marginLeft:8,opacity:.85}}>
                              {pvb.budget>0?`${Math.abs((pvb.delta/pvb.budget)*100).toFixed(1)}%`:""}
                            </span>
                          </div>
                        </div>
                      )}
                      {spread&&(
                        <div style={{flex:"1 1 150px",padding:"9px 16px"}}>
                          <div style={{fontSize:10,color:T.textWeak,letterSpacing:".1em",textTransform:"uppercase" as any,fontWeight:700,marginBottom:2}}
                            title="Coefficient of variation = stddev / mean. Under 10% tight, 10–25% normal, over 25% wide">Spread</div>
                          <div style={{fontSize:16,fontWeight:700,fontVariantNumeric:"tabular-nums",
                            color:spread.cv<0.10?T.positive:spread.cv<0.25?T.textPrimary:T.negative}}>
                            {(spread.cv*100).toFixed(1)}%
                          </div>
                          <div style={{fontSize:10,color:T.textWeak,marginTop:2}} title="Range = High − Low; median across visible partners">
                            {fmt(spread.range)} range · median {fmt(spread.median)}
                          </div>
                        </div>
                      )}
                    </div>
                    {spread&&spread.outliers.length>0&&(
                      <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap" as any,
                        padding:"6px 16px",borderTop:`1px solid ${T.borderPrimary}`,
                        background:`${T.negative}0e`,fontSize:10,color:T.negative}}
                        title="Partners >20% from median — review for scope mismatch or pricing error">
                        <span style={{fontWeight:700}}>&#9888; {spread.outliers.length} outlier{spread.outliers.length!==1?"s":""}</span>
                        {spread.outliers.slice(0,3).map((o,i)=>{
                          const partner=bidPartners.find(p=>p.id===o.pid);
                          return(
                            <span key={o.pid} style={{display:"inline-flex",alignItems:"center",gap:8}}>
                              {i>0&&<span style={{color:T.textMuted}}>&middot;</span>}
                              <span style={{fontWeight:600}}>{partner?.name||"?"} ({o.deltaPct>0?"+":""}{Math.round(o.deltaPct*100)}%)</span>
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
                {/* Column visibility toggles. Nothing to filter before any
                    bid exists, and an inert control on an empty package is
                    part of why every package looked the same. */}
                <div style={{display:partnerIds.length>0?"flex":"none",gap:6,flexWrap:"wrap",marginBottom:16,alignItems:"center"}}>
                  <span style={{fontSize:10,color:T.textWeak,letterSpacing:".1em",textTransform:"uppercase" as any,marginRight:2}}>Show:</span>
                  <span style={{fontSize:10,padding:"3px 10px",borderRadius:10,background:T.bgAccentFaint,color:T.borderAccent,border:`1px solid ${T.borderAccentFaint}`,fontWeight:600}}>Current Budget</span>
                  {partnerIds.map(pid=>{
                    const partner=bidPartners.find(p=>p.id===pid);
                    const hidden=scopeHidden.includes(pid);
                    return(
                      <button key={pid} onClick={()=>setHiddenPartners(prev=>{
                        const cur=prev[scopeId]||[];
                        return{...prev,[scopeId]:hidden?cur.filter(x=>x!==pid):[...cur,pid]};
                      })}
                        style={{fontSize:10,padding:"3px 10px",borderRadius:10,cursor:"pointer",fontFamily:FONT,fontWeight:500,transition:"all .15s",
                          background:hidden?"transparent":T.bgPanel,color:hidden?T.textWeak:T.textSecondary,
                          border:`1px solid ${hidden?T.borderSecondary:T.borderPrimary}`,opacity:hidden?0.5:1}}>
                        {partner?.name||"Unknown"}
                      </button>
                    );
                  })}
                </div>

                {activeBids.length===0&&(
                  <EmptyState T={T}
                    icon={<Icon.FileText size={32}/>}
                    headline="No bids on this trade scope yet"
                    body="Import a subcontractor proposal (AI-parsed or PDF attachment) or enter bid line items manually. Once two bidders are in, the leveling comparison unlocks."
                    ctaLabel="+ Import Bid"
                    onCta={()=>{setBidImportScopeId(activeBidScope);setShowBidImport(true);}}
                  />
                )}

                {activeBids.length>0&&(
                  <div>
                    {/* PDF comparison action bar */}
                    {bidsWithPdf.length>=2&&comparePdfIds.length>0&&(
                      <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:14,padding:"10px 14px",background:T.bgPanel,border:`1px solid ${T.borderPrimary}`,borderRadius:6}}>
                        <span style={{fontSize:11,color:T.textWeak,flex:1}}>
                          {comparePdfIds.length===0?"Select up to 2 proposals below to compare PDFs side by side.":comparePdfIds.length===1?"1 proposal selected — select one more to compare.":"2 proposals selected:"}
                          {comparePdfIds.length===2&&comparePdfIds.map(id=>{
                            const b=bids.find(x=>x.id===id);const p=bidPartners.find(x=>x.id===b?.partnerId);
                            return<span key={id} style={{marginLeft:8,fontSize:10,padding:"1px 8px",background:T.bgAccentLight,color:T.borderAccent,borderRadius:10}}>{p?.name||"?"}</span>;
                          })}
                        </span>
                        {comparePdfIds.length>0&&<button onClick={()=>setComparePdfIds([])} style={{fontSize:10,padding:"2px 8px",border:`1px solid ${T.borderSecondary}`,borderRadius:3,background:"none",color:T.textWeak,cursor:"pointer",fontFamily:FONT}}>Clear</button>}
                        {comparePdfIds.length===2&&<button onClick={()=>openBidPdfViewer(comparePdfIds)} style={{fontSize:11,padding:"6px 16px",border:"none",borderRadius:3,background:T.borderAccent,color:T.textOnGold,cursor:"pointer",fontFamily:FONT,fontWeight:600}}><Icon.FileText size={12}/> Compare PDFs</button>}
                        {comparePdfIds.length===1&&<button onClick={()=>openBidPdfViewer(comparePdfIds)} style={{fontSize:11,padding:"6px 16px",border:`1px solid ${T.borderAccent}`,borderRadius:3,background:"none",color:T.borderAccent,cursor:"pointer",fontFamily:FONT,fontWeight:600}}><Icon.FileText size={12}/> View PDF</button>}
                      </div>
                    )}

                    {/* No overflow here, deliberately.
                        A sticky header pins to its nearest scrolling
                        ancestor. This wrapper used to be styled
                        `overflowX: "auto"`, and CSS computes overflow-y to
                        auto when overflow-x is auto -- so it WAS a scrolling
                        ancestor, just one that never scrolled vertically.
                        The header pinned to a box that never moved and rode
                        up the screen with the rows.

                        Giving the wrapper its own bounded height fixed the
                        pinning and boxed the working pane in: the grid became
                        a window onto the table, clipped at a height the
                        content had no say in.

                        It owns no scrolling now. The tab is bounded, so the
                        pane around it is a real scroll container, the
                        elements between here and it are all `overflow:
                        visible`, and the header pins to the pane. The grid is
                        as tall as its rows, the pane scrolls once, and the
                        header stays put while it does. */}
                    <div style={{border:`1px solid ${T.borderPrimary}`,borderRadius:6}}>
                      <table style={{width:"100%",borderCollapse:"separate",borderSpacing:0,minWidth:400}}>
                        <thead>
                          <tr>
                            {/* Capped, or auto table layout hands the
                                description column every spare pixel and the
                                bidders stay cramped on a wide screen. */}
                            <th style={{fontSize:10,color:T.textWeak,letterSpacing:".1em",textTransform:"uppercase" as any,padding:"8px 10px",textAlign:"left",position:"sticky",left:0,top:-PANE_PAD_T,background:T.bgPanel,zIndex:3,borderBottom:`2px solid ${T.borderPrimary}`,width:"28%",maxWidth:340}}>Line Item</th>
                            <th style={{fontSize:10,color:T.borderAccent,letterSpacing:".1em",textTransform:"uppercase" as any,padding:"8px 10px",textAlign:"right",minWidth:130,verticalAlign:"bottom",position:"sticky",top:-PANE_PAD_T,zIndex:2,
                              borderRight:`1px solid ${T.borderAccentFaint}`,borderBottom:`2px solid ${T.borderPrimary}`,
                              // bgAccentFaint is translucent -- a sticky cell
                              // painted with it let the rows scroll through
                              // the header. The tint is composited over an
                              // opaque base instead of replacing it.
                              backgroundColor:T.bgPanel,
                              backgroundImage:`linear-gradient(${T.bgAccentFaint},${T.bgAccentFaint})`}}>
                              <div style={{display:"flex",flexDirection:"column" as any,alignItems:"flex-end",gap:2}}>
                                <span>Current Budget</span>
                                <span style={{fontSize:10,color:T.textMuted,fontWeight:400,textTransform:"none" as any,letterSpacing:0}}>from estimate</span>
                              </div>
                            </th>
                            {visiblePartnerIds.map(pid=>{
                              const partner=bidPartners.find(p=>p.id===pid);
                              const isLow=partnerColumnTotals[pid]===lowestVisibleTotal&&visiblePartnerIds.length>1;
                              const bidId=partnerBidId[pid];
                              const hasPdf=!!(bidId&&bidsWithPdf.includes(pid));
                              const isSelected=!!(bidId&&comparePdfIds.includes(bidId));
                              return(
                                <th key={pid} style={{padding:"6px 10px",textAlign:"center",verticalAlign:"bottom",
                                  // Three bidders is the common comparison and
                                  // it should never need sideways scrolling,
                                  // so up to three the columns are allowed to
                                  // squeeze. Past that the sheet is wide by
                                  // nature and a comfortable column beats a
                                  // cramped one.
                                  minWidth:visiblePartnerIds.length<=3?118:150,
                                  position:"sticky",top:-PANE_PAD_T,zIndex:2,
                                  // Opaque, or the rows scroll through it.
                                  background:T.bgPanel,
                                  borderBottom:`2px solid ${T.borderPrimary}`,
                                  // A rule per bidder, so a column stays a
                                  // column all the way down the page.
                                  borderLeft:`1px solid ${T.borderPrimary}`}}>
                                  <div style={{display:"flex",flexDirection:"column" as any,alignItems:"stretch",gap:6}}>
                                    <span style={{fontSize:10,color:isLow?T.positive:T.textSecondary,fontWeight:600,textAlign:"center"}}>{partner?.name||"Unknown"}</span>
                                    {hasPdf&&bidId&&(
                                      <button onClick={()=>setComparePdfIds(prev=>{
                                        if(prev.includes(bidId))return prev.filter(x=>x!==bidId);
                                        if(prev.length>=2)return[prev[1],bidId];
                                        return[...prev,bidId];
                                      })} style={{width:"100%",fontSize:10,fontWeight:600,padding:"5px 8px",borderRadius:3,cursor:"pointer",fontFamily:FONT,textAlign:"center",
                                        border:`1px solid ${isSelected?T.borderAccent:T.borderAccent+"55"}`,
                                        background:isSelected?T.borderAccent:T.bgAccentFaint,
                                        color:isSelected?T.textOnGold:T.borderAccent,
                                        transition:"all .15s"}}>
                                        {isSelected?"✓ PDF Selected":<><Icon.FileText size={12}/> View / Compare</>}
                                      </button>
                                    )}
                                  </div>
                                </th>
                              );
                            })}
                          </tr>
                        </thead>
                        <tbody>
                          {/* ── Estimate rows (drag targets) ── */}
                          {alignedRows.map(row=>{
                            const isDrop=dropTargetEstItemId===row.itemId;
                            const isDragging=dragBidItemIds!==null;
                            // Basis info for this estimate item
                            const li=itemState[row.itemId]||{};
                            // Not `|| "Hard Bid"`: a line whose status was
                            // cleared is not a hard bid, and saying so here is
                            // the tool filling in an answer the estimator
                            // deliberately removed.
                            const cs=displayStatus(li.costStatus);
                            const srcBid=li.sourceBidId?bids.find(b=>b.id===li.sourceBidId):null;
                            // Infer bid from tradeName+tradeDate when sourceBidId isn't set (sandbox / manual attribution)
                            const inferredBid=!srcBid&&li.tradeName&&li.tradeDate
                              ?bids.find(b=>{const p=bidPartners.find(x=>x.id===b.partnerId);return p?.name===li.tradeName&&b.date===li.tradeDate;})
                              :null;
                            const effectiveBid=srcBid||inferredBid;
                            const srcPartner=effectiveBid?bidPartners.find(p=>p.id===effectiveBid.partnerId):null;
                            // null when there is nothing true to say. A line
                            // with no status and no attribution used to fall
                            // through to "Hard Bid", which asserted the one
                            // thing the estimator had just taken back.
                            const basisLabel=srcPartner
                              ?srcPartner.name.split(" ")[0]
                              :li.tradeName
                              ?String(li.tradeName).split(" ")[0]
                              :cs?cs
                              :null;
                            const basisColor=cs==="Allowance"?"#d4a76a":cs==="Needs Review"?T.negative:(srcPartner||li.tradeName)?T.borderAccent:T.textMuted;
                            return(
                              <React.Fragment key={row.itemId}>
                              <tr
                                onDragOver={e=>{e.preventDefault();setDropTargetEstItemId(row.itemId);}}
                                onDragLeave={e=>{if(e.currentTarget&&!e.currentTarget.contains(e.relatedTarget as Node))setDropTargetEstItemId(null);}}
                                onDrop={e=>{e.preventDefault();try{doAlign(JSON.parse(e.dataTransfer.getData("text/plain")),row.itemId);}catch(err){}}}
                                style={{borderBottom:`1px solid ${T.borderPrimary}22`,
                                  background:isDrop?`${T.borderAccent}14`:isDragging?`${T.borderAccent}06`:"transparent",
                                  outline:isDrop?`2px dashed ${T.borderAccent}55`:isDragging?`1px dashed ${T.borderAccent}28`:"none",
                                  outlineOffset:-1,transition:"background .1s"}}>
                                <td style={{padding:"7px 10px 7px 16px",fontSize:11,color:T.textTertiary,position:"sticky",left:0,
                                  background:isDrop?`${T.borderAccent}14`:T.bgPanel,zIndex:1,transition:"background .1s",minWidth:220}}>
                                  <div>
                                    <span style={{color:T.textMuted,fontSize:10,marginRight:6}}>{row.divName.replace(/^Div \d+ – /,"").replace(/^Division \d+ – /,"")}</span>
                                    {row.desc}
                                  </div>
                                  {Object.keys(row.partnerAmounts).length>0&&(
                                    <div style={{marginTop:4}}>
                                      <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
                                        {Object.entries(row.partnerAmounts).map(([pid,data])=>{
                                          const partner=bidPartners.find(p=>p.id===pid);
                                          return(
                                            <span key={pid}
                                              style={{display:"inline-flex",alignItems:"center",borderRadius:3,overflow:"hidden",
                                                border:`1px solid ${T.borderAccentFaint}`,fontSize:10,color:T.borderAccent}}>
                                              {/* drag handle + label */}
                                              <span
                                                draggable
                                                title={`${partner?.name||"?"} — drag to move to a different estimate line`}
                                                onDragStart={e=>{
                                                  e.dataTransfer.setData("text/plain",JSON.stringify((data as any).bidItemIds));
                                                  e.dataTransfer.effectAllowed="move";
                                                  setDragBidItemIds((data as any).bidItemIds);
                                                }}
                                                onDragEnd={()=>{setDragBidItemIds(null);setDropTargetEstItemId(null);}}
                                                style={{display:"inline-flex",alignItems:"center",gap:3,padding:"2px 5px 2px 6px",cursor:"grab",
                                                  background:`${T.borderAccent}18`}}>
                                                ⠿ {partner?.name?.split(" ")[0]||"?"}
                                              </span>
                                              {/* unassign button — separate clickable area, not inside the draggable */}
                                              <button
                                                onClick={e=>{e.stopPropagation();doUnalign((data as any).bidItemIds);}}
                                                title="Unassign — returns to Unassigned Bid Items (undoable with Ctrl+Z)"
                                                style={{display:"inline-flex",alignItems:"center",justifyContent:"center",
                                                  width:18,height:"100%",padding:0,cursor:"pointer",fontFamily:FONT,
                                                  background:`${T.borderAccent}28`,border:"none",borderLeft:`1px solid ${T.borderAccentFaint}`,
                                                  color:T.borderAccent}}>
                                                <Icon.X size={9}/>
                                              </button>
                                            </span>
                                          );
                                        })}
                                      </div>
                                      <div style={{fontSize:10,color:T.textMuted,marginTop:3,fontStyle:"italic"}}>
                                        Drag chip to move · × to unassign
                                      </div>
                                    </div>
                                  )}
                                  {/* Expand toggle — show when any bid items are aligned */}
                                  {(()=>{
                                    const allBidItemIds=Object.values(row.partnerAmounts).flatMap((d:any)=>d.bidItemIds);
                                    if(allBidItemIds.length===0)return null;
                                    const isExpanded=expandedAlignRows.has(row.itemId);
                                    return(
                                      <button
                                        onClick={e=>{e.stopPropagation();setExpandedAlignRows(prev=>{const n=new Set(prev);n.has(row.itemId)?n.delete(row.itemId):n.add(row.itemId);return n;});}}
                                        style={{marginTop:5,display:"inline-flex",alignItems:"center",gap:4,fontSize:10,
                                          background:"none",border:"none",color:isExpanded?T.borderAccent:T.textWeak,
                                          cursor:"pointer",padding:0,fontFamily:FONT,letterSpacing:".02em"}}>
                                        <span style={{fontSize:10,lineHeight:1,transition:"transform .15s",
                                          display:"inline-block",transform:isExpanded?"rotate(90deg)":"rotate(0deg)"}}>▸</span>
                                        {allBidItemIds.length} bid line{allBidItemIds.length!==1?"s":""} combined
                                      </button>
                                    );
                                  })()}
                                  {isDrop&&<div style={{fontSize:10,color:T.borderAccent,marginTop:4,opacity:.8}}>Drop to align here</div>}
                                </td>
                                {/* Current Budget column — amount + basis + version/date + PDF link */}
                                {(()=>{
                                  const hasPdfEff=!!(effectiveBid&&(effectiveBid.pdfUrl||effectiveBid.pdfStoragePath||bidPdfCache.current.has(effectiveBid.id)));
                                  const refDate=effectiveBid?.date||li.tradeDate||null;
                                  const refDateFmt=refDate?new Date(refDate+"T00:00:00").toLocaleDateString("en-US",{month:"short",day:"numeric",year:"2-digit"}as any):"";
                                  const versionStr=effectiveBid?`v${effectiveBid.version} · `:"";
                                  return(
                                    <td style={{padding:"7px 10px",textAlign:"right",verticalAlign:"top",
                                      background:isDrop?`${T.borderAccent}14`:T.bgAccentFaint,
                                      borderRight:`1px solid ${T.borderAccentFaint}`,transition:"background .1s"}}>
                                      <div style={{display:"flex",flexDirection:"column" as any,alignItems:"flex-end",gap:3}}>
                                        <span style={{fontSize:11,fontVariantNumeric:"tabular-nums",color:T.borderAccent,fontWeight:500}}>{fmt(row.amt)}</span>
                                        {basisLabel!==null&&<span style={{display:"inline-flex",alignItems:"center",gap:3,fontSize:10,padding:"1px 4px 1px 5px",borderRadius:3,fontWeight:600,letterSpacing:".03em",
                                          background:`${basisColor}18`,border:`1px solid ${basisColor}44`,color:basisColor}}>
                                          {basisLabel}
                                          {(effectiveBid||li.tradeName||li.sourceBidId)&&(
                                            <button
                                              title="Clear bid attribution (keeps current cost)"
                                              onClick={()=>{
                                                snapshot();
                                                setItemState(prev=>({...prev,[row.itemId]:{...prev[row.itemId],sourceBidId:undefined,tradeName:undefined,tradeDate:undefined}}));
                                              }}
                                              style={{background:"none",border:"none",color:"inherit",cursor:"pointer",fontSize:10,lineHeight:1,padding:"0 1px",opacity:.65,fontFamily:FONT}}
                                            ><Icon.X size={10}/></button>
                                          )}
                                        </span>}
                                        <span
                                          onClick={hasPdfEff&&effectiveBid?()=>viewBidPdf(effectiveBid):undefined}
                                          style={{fontSize:10,color:hasPdfEff?T.borderAccent:T.textMuted,
                                            cursor:hasPdfEff?"pointer":"default",
                                            textDecoration:hasPdfEff?"underline":"none",
                                            opacity:refDateFmt?.85:0,
                                            userSelect:"none",
                                            minHeight:"1em"}}>
                                          {versionStr}{refDateFmt}
                                        </span>
                                      </div>
                                    </td>
                                  );
                                })()}
                                {(()=>{
                                  // Per-row low/high across partners
                                  const rowVals=visiblePartnerIds.map(pid=>row.partnerAmounts[pid]?.total||0).filter(v=>v>0);
                                  const rowMin=rowVals.length?Math.min(...rowVals):0;
                                  const rowMax=rowVals.length>1?Math.max(...rowVals):0;
                                  return visiblePartnerIds.map(pid=>{
                                    const data=row.partnerAmounts[pid];
                                    const val=data?.total||0;
                                    const isLow=val>0&&val===rowMin&&rowVals.length>1;
                                    const isHigh=val>0&&val===rowMax&&rowMax>rowMin;
                                    const pctAboveLow=isLow||!rowMin?0:Math.round(((val-rowMin)/rowMin)*100);
                                    // Is this partner the current accepted source?
                                    const isAccepted=!!(srcBid&&srcBid.partnerId===pid);
                                    return(
                                      <td key={pid} style={{padding:"7px 10px",textAlign:"right",verticalAlign:"top",minWidth:130,
                                        background:isLow?`${T.positive}0a`:isHigh?`${T.negative}08`:"transparent",
                                        transition:"background .1s"}}>
                                        {val?(
                                          <div style={{display:"flex",flexDirection:"column" as any,alignItems:"flex-end",gap:3}}>
                                            <span style={{fontSize:11,fontWeight:500,fontVariantNumeric:"tabular-nums",
                                              color:isLow?T.positive:isHigh?T.negative:T.textSecondary}}>
                                              {fmt(val)}
                                            </span>
                                            {pctAboveLow>0&&(
                                              <span style={{fontSize:10,padding:"1px 5px",borderRadius:10,fontWeight:600,
                                                background:`${T.negative}15`,color:T.negative}}>
                                                +{pctAboveLow}%
                                              </span>
                                            )}
                                            {isLow&&rowVals.length>1&&(
                                              <span style={{fontSize:10,padding:"1px 5px",borderRadius:10,fontWeight:700,
                                                background:`${T.positive}18`,color:T.positive,letterSpacing:".03em"}}>
                                                LOW
                                              </span>
                                            )}
                                            {isAccepted?(
                                              <span style={{fontSize:10,padding:"2px 7px",borderRadius:3,fontFamily:FONT,
                                                border:`1px solid ${T.positive}55`,background:`${T.positive}12`,
                                                color:T.positive,fontWeight:700,letterSpacing:".03em"}}>
                                                ✓ Basis
                                              </span>
                                            ):(
                                              <button onClick={()=>doAccept(row.itemId,pid,data!)}
                                                style={{fontSize:10,padding:"2px 7px",borderRadius:3,cursor:"pointer",fontFamily:FONT,
                                                  border:`1px solid ${T.borderAccentFaint}`,background:`${T.borderAccent}12`,
                                                  color:T.borderAccent,fontWeight:600,letterSpacing:".03em"}}>
                                                Accept
                                              </button>
                                            )}
                                          </div>
                                        ):<span style={{fontSize:11,color:T.textMuted}}>—</span>}
                                      </td>
                                    );
                                  });
                                })()}
                              </tr>
                            {/* ── Expanded breakdown sub-rows ── */}
                            {expandedAlignRows.has(row.itemId)&&(()=>{
                              // Collect every unique bid item that is aligned to this estimate row
                              type SubItem={bidItemId:string;desc:string;qty:number;unit:string;unitCost:number;partnerAmounts:Record<string,number>};
                              const subItems:SubItem[]=[];
                              const seen=new Set<string>();
                              activeBids.forEach(bid=>{
                                if(!row.partnerAmounts[bid.partnerId])return;
                                (bid.items||[]).forEach((bi:any)=>{
                                  if(!row.partnerAmounts[bid.partnerId]?.bidItemIds.includes(bi.id))return;
                                  if(!seen.has(bi.id)){
                                    seen.add(bi.id);
                                    subItems.push({bidItemId:bi.id,desc:bi.description||"—",qty:bi.qty||1,unit:bi.unit||"LS",unitCost:bi.unitCost||0,partnerAmounts:{}});
                                  }
                                  subItems.find(x=>x.bidItemId===bi.id)!.partnerAmounts[bid.partnerId]=(bi.qty||1)*(bi.unitCost||0);
                                });
                              });
                              return subItems.map((item,si)=>(
                                <tr key={item.bidItemId} style={{borderBottom:si<subItems.length-1?`1px solid ${T.borderPrimary}18`:`2px solid ${T.borderPrimary}33`}}>
                                  <td style={{padding:"4px 10px 4px 28px",fontSize:10,color:T.textWeak,position:"sticky",left:0,background:T.bgApp,zIndex:1,minWidth:220}}>
                                    <div style={{display:"flex",alignItems:"center",gap:6}}>
                                      <span style={{color:T.borderAccentFaint,userSelect:"none" as any,flexShrink:0}}>↳</span>
                                      <span style={{fontStyle:"italic",flex:1}}>{item.desc}</span>
                                      {item.qty!==1||item.unit!=="LS"?<span style={{fontSize:10,color:T.textMuted,flexShrink:0}}>{item.qty!==1?`${item.qty} ${item.unit}`:item.unit}{item.unitCost?" @ $"+item.unitCost.toLocaleString():""}</span>:null}
                                      <button
                                        onClick={e=>{e.stopPropagation();doUnalign([item.bidItemId]);}}
                                        title="Unassign this line — returns it to Unassigned Bid Items (undoable)"
                                        style={{flexShrink:0,display:"inline-flex",alignItems:"center",gap:3,fontSize:10,padding:"1px 5px",borderRadius:3,cursor:"pointer",fontFamily:FONT,
                                          border:`1px solid ${T.borderPrimary}44`,background:"transparent",color:T.textMuted,
                                          opacity:0.7}}
                                        onMouseEnter={e=>{(e.currentTarget as HTMLElement).style.color=T.negative;(e.currentTarget as HTMLElement).style.borderColor=T.negative+"66";(e.currentTarget as HTMLElement).style.opacity="1";}}
                                        onMouseLeave={e=>{(e.currentTarget as HTMLElement).style.color=T.textMuted;(e.currentTarget as HTMLElement).style.borderColor=T.borderPrimary+"44";(e.currentTarget as HTMLElement).style.opacity="0.7";}}>
                                        ↩ Unassign
                                      </button>
                                    </div>
                                  </td>
                                  <td style={{background:`${T.bgAccentFaint}88`,borderRight:`1px solid ${T.borderAccentFaint}`}}/>
                                  {visiblePartnerIds.map(pid=>(
                                    <td key={pid} style={{padding:"4px 10px",textAlign:"right",fontSize:10,
                                      color:item.partnerAmounts[pid]?T.textWeak:T.textMuted,
                                      background:item.partnerAmounts[pid]?"transparent":`${T.borderPrimary}08`}}>
                                      {item.partnerAmounts[pid]?fmt(item.partnerAmounts[pid]):"—"}
                                    </td>
                                  ))}
                                </tr>
                              ));
                            })()}
                              </React.Fragment>
                            );
                          })}

                          {/* ── Unassigned section ── */}
                          {unassignedRows.length>0&&(
                            <tr style={{background:T.bgPanel}}>
                              <td colSpan={2+visiblePartnerIds.length} style={{padding:"10px 14px",borderTop:`2px solid ${T.borderPrimary}`,borderBottom:`1px solid ${T.borderPrimary}`}}>
                                <div style={{display:"flex",alignItems:"center",gap:10}}>
                                  <span style={{fontSize:10,fontWeight:700,color:T.textWeak,letterSpacing:".1em",textTransform:"uppercase" as any}}>Unassigned Bid Items</span>
                                  <span style={{fontSize:10,color:T.textMuted,fontStyle:"italic"}}>Drag a row onto an estimate line above to align it, or click "+ Add to Estimate" to create a new line item</span>
                                </div>
                              </td>
                            </tr>
                          )}
                          {unassignedRows.map((row,ri)=>{
                            const vals=visiblePartnerIds.map(pid=>row.partnerTotals[pid]||0);
                            const nonZero=vals.filter(v=>v>0);
                            const minVal=nonZero.length?Math.min(...nonZero):0;
                            const maxVal=nonZero.length>1?Math.max(...nonZero):0;
                            const isThisDragging=!!(dragBidItemIds&&row.bidItemIds.some(id=>dragBidItemIds.includes(id)));
                            return(
                              <tr key={ri}
                                draggable
                                onDragStart={e=>{
                                  e.dataTransfer.setData("text/plain",JSON.stringify(row.bidItemIds));
                                  e.dataTransfer.effectAllowed="move";
                                  setDragBidItemIds(row.bidItemIds);
                                }}
                                onDragEnd={()=>{setDragBidItemIds(null);setDropTargetEstItemId(null);}}
                                style={{borderBottom:`1px solid ${T.borderPrimary}22`,cursor:"grab",
                                  background:isThisDragging?`${T.borderAccent}0c`:"transparent",
                                  opacity:isThisDragging?0.55:1,transition:"all .1s"}}
                                onMouseEnter={e=>{if(!dragBidItemIds)(e.currentTarget as HTMLElement).style.background=`${T.borderAccent}0a`;}}
                                onMouseLeave={e=>{if(!dragBidItemIds)(e.currentTarget as HTMLElement).style.background="transparent";}}>
                                <td style={{padding:"7px 10px",fontSize:11,color:T.textSecondary,position:"sticky",left:0,background:"inherit",zIndex:1}}>
                                  <div style={{display:"flex",alignItems:"center",gap:8}}>
                                    <span
                                      title="Drag this row onto an estimate line above to align it"
                                      style={{color:T.borderAccent,fontSize:14,lineHeight:1,cursor:"grab",userSelect:"none" as any,flexShrink:0,
                                        opacity:.7,letterSpacing:"-1px",padding:"2px 3px",borderRadius:3,
                                        background:`${T.borderAccent}10`,border:`1px solid ${T.borderAccentFaint}`}}>⠿</span>
                                    <span style={{flex:1}}>{row.desc}</span>
                                  </div>
                                </td>
                                <td style={{padding:"7px 10px",textAlign:"right",background:T.bgAccentFaint,borderRight:`1px solid ${T.borderAccentFaint}`}}>
                                  <button onClick={e=>{e.stopPropagation();doAddToEstimate(row);}}
                                    title="Add to estimate"
                                    style={{fontSize:10,padding:"2px 7px",borderRadius:3,cursor:"pointer",fontFamily:FONT,whiteSpace:"nowrap" as any,
                                      border:`1px solid ${T.borderSecondary}`,background:"transparent",color:T.textMuted,fontWeight:400,opacity:.7}}
                                    onMouseEnter={e=>{e.currentTarget.style.opacity="1";e.currentTarget.style.color=T.borderAccent;}}
                                    onMouseLeave={e=>{e.currentTarget.style.opacity=".7";e.currentTarget.style.color=T.textMuted;}}>
                                    + estimate
                                  </button>
                                </td>
                                {visiblePartnerIds.map(pid=>{
                                  const val=row.partnerTotals[pid];
                                  const isLow=val===minVal&&nonZero.length>1&&(val||0)>0;
                                  const isHigh=val===maxVal&&maxVal>minVal&&(val||0)>0;
                                  return<td key={pid} style={{padding:"7px 10px",fontSize:11,textAlign:"right",fontVariantNumeric:"tabular-nums",fontWeight:500,
                                    color:!val?T.textMuted:isLow?T.positive:isHigh?T.negative:T.textTertiary,
                                    background:isLow?`${T.positive}08`:isHigh?`${T.negative}08`:"transparent"}}>
                                    {val?fmt(val):"—"}
                                  </td>;
                                })}
                              </tr>
                            );
                          })}
                          {/* ── Leveling Adjustments section ── */}
                          <tr style={{background:T.bgPanel}}>
                            <td colSpan={2+visiblePartnerIds.length} style={{padding:"10px 14px",borderTop:`2px solid ${T.borderPrimary}`,borderBottom:`1px solid ${T.borderPrimary}`}}>
                              <div style={{display:"flex",alignItems:"center",gap:10}}>
                                <span style={{fontSize:10,fontWeight:700,color:T.textWeak,letterSpacing:".1em",textTransform:"uppercase" as any}}>Leveling Adjustments</span>
                                <span style={{fontSize:10,color:T.textMuted,fontStyle:"italic"}}>Note scope items not priced by each bidder — enter an amount to level their total for an apples-to-apples comparison</span>
                                <button onClick={()=>{
                                  // Insert standard checklist rows not already present (case-insensitive match)
                                  const existing=new Set(scopeLevelingAdjs.map(a=>a.description.trim().toLowerCase()));
                                  const missing=STANDARD_LEVELING_ITEMS.filter(s=>!existing.has(s.toLowerCase()));
                                  if(!missing.length){showToast("All standard leveling items already present");return;}
                                  setLevelingAdjustments(prev=>[...prev,...missing.map(desc=>({id:uid(),scopeId:scopeId,description:desc,partners:{}}))]);
                                  showToast(`Added ${missing.length} standard leveling item${missing.length!==1?"s":""}`);
                                }} style={{marginLeft:"auto",fontSize:10,padding:"3px 10px",borderRadius:3,cursor:"pointer",fontFamily:FONT,fontWeight:600,
                                  border:`1px solid ${T.borderAccentFaint}`,background:`${T.borderAccent}12`,color:T.borderAccent,whiteSpace:"nowrap" as any}}
                                  title="Insert the standard GC leveling checklist: general conditions, bonds, insurance, cleanup, hoisting, tax, permits, escalation">
                                  + Standard Checklist
                                </button>
                                <button onClick={()=>{
                                  const newAdj:LevelingAdjustment={id:uid(),scopeId:scopeId,description:"",partners:{}};
                                  setLevelingAdjustments(prev=>[...prev,newAdj]);
                                }} style={{fontSize:10,padding:"3px 10px",borderRadius:3,cursor:"pointer",fontFamily:FONT,fontWeight:600,
                                  border:`1px solid ${T.borderAccentFaint}`,background:`${T.borderAccent}12`,color:T.borderAccent,whiteSpace:"nowrap" as any}}>
                                  + Add Item
                                </button>
                              </div>
                            </td>
                          </tr>
                          {scopeLevelingAdjs.map(adj=>(
                            <tr key={adj.id} style={{borderBottom:`1px solid ${T.borderPrimary}22`}}>
                              {/* Description cell */}
                              <td style={{padding:"6px 10px",position:"sticky",left:0,background:T.bgPanel,zIndex:1,minWidth:220}}>
                                <div style={{display:"flex",alignItems:"center",gap:6}}>
                                  <input
                                    value={adj.description}
                                    onChange={e=>setLevelingAdjustments(prev=>prev.map(a=>a.id===adj.id?{...a,description:e.target.value}:a))}
                                    placeholder="e.g. Temporary power"
                                    style={{flex:1,fontSize:11,background:"none",border:"none",borderBottom:`1px solid ${T.borderSecondary}`,color:T.textSecondary,
                                      fontFamily:FONT,padding:"2px 0",outline:"none",minWidth:0}}
                                  />
                                  <button onClick={()=>setLevelingAdjustments(prev=>prev.filter(a=>a.id!==adj.id))}
                                    style={{fontSize:10,color:T.textWeak,background:"none",border:"none",cursor:"pointer",flexShrink:0,padding:"0 2px",opacity:.5,fontFamily:FONT}}
                                    onMouseEnter={e=>(e.currentTarget as HTMLElement).style.opacity="1"}
                                    onMouseLeave={e=>(e.currentTarget as HTMLElement).style.opacity=".5"}><Icon.X size={10}/></button>
                                </div>
                              </td>
                              {/* Blank for estimate column */}
                              <td style={{background:T.bgAccentFaint,borderRight:`1px solid ${T.borderAccentFaint}`}}/>
                              {/* Per-partner cells */}
                              {visiblePartnerIds.map(pid=>{
                                const pData=adj.partners[pid]||{included:true,amount:0};
                                const isExcluded=!pData.included;
                                return(
                                  <td key={pid} style={{padding:"6px 10px",textAlign:"right",verticalAlign:"middle"}}>
                                    <div style={{display:"flex",alignItems:"center",justifyContent:"flex-end",gap:6}}>
                                      {isExcluded?(
                                        <input
                                          type="number"
                                          min="0"
                                          value={pData.amount||""}
                                          placeholder="add amount"
                                          onChange={e=>setLevelingAdjustments(prev=>prev.map(a=>a.id===adj.id?{
                                            ...a,partners:{...a.partners,[pid]:{...pData,amount:parseFloat(e.target.value)||0}}
                                          }:a))}
                                          style={{width:90,fontSize:11,background:"none",border:"none",borderBottom:`1px solid ${T.borderSecondary}`,
                                            color:T.textSecondary,fontFamily:FONT,textAlign:"right",outline:"none",padding:"1px 0"}}
                                        />
                                      ):(
                                        <span style={{fontSize:10,color:T.textMuted}}>incl.</span>
                                      )}
                                      <button onClick={()=>setLevelingAdjustments(prev=>prev.map(a=>a.id===adj.id?{
                                        ...a,partners:{...a.partners,[pid]:{...pData,included:!pData.included}}
                                      }:a))}
                                        title={isExcluded?"Mark as included by this bidder":"Mark as not in this bid"}
                                        style={{fontSize:10,padding:"1px 5px",borderRadius:3,cursor:"pointer",fontFamily:FONT,
                                          border:`1px solid ${T.borderSecondary}`,background:"transparent",
                                          color:isExcluded?T.negative:T.textMuted,transition:"all .12s"}}>
                                        {isExcluded?"✕":"✓"}
                                      </button>
                                    </div>
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr style={{borderTop:`2px solid ${T.borderPrimary}`}}>
                            <td style={{padding:"10px",fontSize:11,fontWeight:700,color:T.textPrimary,position:"sticky",left:0,background:T.bgPanel,zIndex:1}}>
                              <div>TOTAL</div>
                              <div style={{fontSize:10,color:T.textWeak,fontWeight:400,marginTop:2}}>Bid · Adj · Leveled</div>
                            </td>
                            <td style={{padding:"10px",textAlign:"right",background:T.bgAccentFaint,borderRight:`1px solid ${T.borderAccentFaint}`}}>
                              {estimateScopeTotal>0?(
                                <div style={{display:"flex",flexDirection:"column" as any,alignItems:"flex-end",gap:3}}>
                                  <span style={{fontSize:13,fontWeight:700,color:T.borderAccent,fontVariantNumeric:"tabular-nums"}}>{fmt(estimateScopeTotal)}</span>
                                  {lowestLeveledTotal>0&&(()=>{
                                    const delta=lowestLeveledTotal-estimateScopeTotal;
                                    const absDelta=Math.abs(delta);
                                    if(absDelta<100)return null;
                                    const over=delta>0;
                                    return(
                                      <span style={{fontSize:10,color:over?T.negative:T.positive,fontWeight:500}}>
                                        {over?`▲`:`▼`} {fmt(absDelta)} {over?"over":"under"}
                                      </span>
                                    );
                                  })()}
                                </div>
                              ):<span style={{fontSize:12,color:T.textMuted}}>—</span>}
                            </td>
                            {visiblePartnerIds.map(pid=>{
                              const raw=partnerColumnTotals[pid];
                              const adj=getTotalAdj(pid);
                              const leveled=leveledTotals[pid];
                              const isLow=leveled===lowestLeveledTotal&&visiblePartnerIds.length>1&&leveled>0;
                              return(
                                <td key={pid} style={{padding:"10px",textAlign:"right",fontVariantNumeric:"tabular-nums",
                                  background:isLow?`${T.positive}06`:"transparent"}}>
                                  <div style={{display:"flex",flexDirection:"column" as any,alignItems:"flex-end",gap:2}}>
                                    {adj>0&&<span style={{fontSize:10,color:T.textWeak,fontWeight:400}}>{fmt(raw)} +{fmt(adj)}</span>}
                                    <span style={{fontSize:13,fontWeight:700,color:isLow?T.positive:T.textPrimary}}>
                                      {fmt(leveled)}
                                    </span>
                                    {!isLow&&leveled>0&&lowestLeveledTotal>0&&leveled>lowestLeveledTotal&&(
                                      <span style={{fontSize:10,color:T.negative,fontWeight:500}}>
                                        +{Math.round(((leveled-lowestLeveledTotal)/lowestLeveledTotal)*100)}%
                                      </span>
                                    )}
                                  </div>
                                </td>
                              );
                            })}
                          </tr>
                          <tr style={{background:T.bgPanel,borderTop:`1px solid ${T.borderPrimary}33`}}>
                            <td colSpan={2} style={{padding:"8px 10px",fontSize:10,color:T.textWeak,fontWeight:700,letterSpacing:".1em",textTransform:"uppercase" as any,position:"sticky",left:0,background:T.bgPanel,zIndex:1}}>
                              Use Entire Bid →
                            </td>
                            {visiblePartnerIds.map(pid=>{
                              const hasAligned=alignedRows.some(r=>r.partnerAmounts[pid]?.total>0);
                              return(
                                <td key={pid} style={{padding:"8px 10px",textAlign:"center"}}>
                                  {hasAligned?(
                                    <button onClick={()=>doAcceptAll(pid)}
                                      style={{fontSize:10,padding:"4px 12px",borderRadius:3,cursor:"pointer",fontFamily:FONT,fontWeight:600,
                                        border:`1px solid ${T.borderAccent}`,background:`${T.borderAccent}15`,color:T.borderAccent,
                                        transition:"all .15s",whiteSpace:"nowrap" as any}}
                                      onMouseEnter={e=>{(e.currentTarget as HTMLElement).style.background=T.borderAccent;(e.currentTarget as HTMLElement).style.color=T.textOnGold;}}
                                      onMouseLeave={e=>{(e.currentTarget as HTMLElement).style.background=`${T.borderAccent}15`;(e.currentTarget as HTMLElement).style.color=T.borderAccent;}}>
                                      Accept All
                                    </button>
                                  ):<span style={{fontSize:10,color:T.textMuted}}>—</span>}
                                </td>
                              );
                            })}
                          </tr>
                          {/* One-click award: leveled total → buyout row, no re-typing */}
                          <tr style={{background:T.bgPanel,borderTop:`1px solid ${T.borderPrimary}33`}}>
                            <td colSpan={2} style={{padding:"8px 10px",fontSize:10,color:T.textWeak,fontWeight:700,letterSpacing:".1em",textTransform:"uppercase" as any,position:"sticky",left:0,background:T.bgPanel,zIndex:1}}>
                              Award Scope →
                            </td>
                            {visiblePartnerIds.map(pid=>{
                              const partner=bidPartners.find(p=>p.id===pid);
                              const leveled=leveledTotals[pid]||0;
                              const isLow=leveled===lowestLeveledTotal&&leveled>0;
                              const bidId=partnerBidId[pid]||"";
                              return(
                                <td key={pid} style={{padding:"8px 10px",textAlign:"center"}}>
                                  {leveled>0?(
                                    <button onClick={()=>onAwardBid({
                                        scopeName:activeScope.name,
                                        scopeDivisionIds:activeScope.divisionIds||[],
                                        partnerName:partner?.name||"Unknown",
                                        bidId,
                                        leveledTotal:leveled,
                                        budget:estimateScopeTotal,
                                      })}
                                      title={`Award "${activeScope.name}" to ${partner?.name||"this partner"} at ${fmt(leveled)} — creates an awarded Buyout row`}
                                      style={{fontSize:10,padding:"4px 12px",borderRadius:3,cursor:"pointer",fontFamily:FONT,fontWeight:700,
                                        border:`1px solid ${isLow?T.positive:T.borderSecondary}`,
                                        background:isLow?`${T.positive}15`:"transparent",
                                        color:isLow?T.positive:T.textWeak,
                                        transition:"all .15s",whiteSpace:"nowrap" as any}}
                                      onMouseEnter={e=>{(e.currentTarget as HTMLElement).style.background=isLow?T.positive:T.bgHover;if(isLow)(e.currentTarget as HTMLElement).style.color="#fff";}}
                                      onMouseLeave={e=>{(e.currentTarget as HTMLElement).style.background=isLow?`${T.positive}15`:"transparent";(e.currentTarget as HTMLElement).style.color=isLow?T.positive:T.textWeak;}}>
                                      {isLow?"★ Award":"Award"}
                                    </button>
                                  ):<span style={{fontSize:10,color:T.textMuted}}>—</span>}
                                </td>
                              );
                            })}
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          {/* ── HISTORY SUB-TAB ── */}
          {bidSubTab==="history"&&(
            <div>
              {Object.keys(historyByPartner).length===0&&(
                <div style={{padding:"40px 0",textAlign:"center" as any,color:T.textMuted}}>
                  <div style={{fontSize:11}}>No bid history for this scope yet.</div>
                </div>
              )}
              {Object.entries(historyByPartner).map(([partnerId,partnerBids])=>{
                const partner=bidPartners.find(p=>p.id===partnerId);
                const isCollapsed=collapsedHistoryPartners.has(partnerId);
                const toggleCollapsed=()=>setCollapsedHistoryPartners(prev=>{
                  const next=new Set(prev);
                  next.has(partnerId)?next.delete(partnerId):next.add(partnerId);
                  return next;
                });
                return(
                  <div key={partnerId} style={{marginBottom:28}}>
                    <div onClick={toggleCollapsed} style={{fontSize:13,fontWeight:700,color:T.textPrimary,marginBottom:isCollapsed?0:10,display:"flex",alignItems:"center",gap:10,cursor:"pointer",userSelect:"none" as any}}>
                      <span style={{fontSize:10,color:T.textWeak,transition:"transform .2s",display:"inline-block",transform:isCollapsed?"rotate(-90deg)":"rotate(0deg)"}}>▾</span>
                      {partner?.name||"Unknown"}
                      {partner?.trade&&<span style={{fontSize:10,color:T.textWeak,fontWeight:400,letterSpacing:".08em",textTransform:"uppercase"}}>{partner.trade}</span>}
                    </div>
                    {!isCollapsed&&<div style={{display:"flex",flexDirection:"column" as any,gap:6}}>
                      {partnerBids.map((b,bi)=>{
                        const t=bidTotal(b);
                        const prev=partnerBids[bi+1];
                        const prevTotal=prev?bidTotal(prev):null;
                        const delta=prevTotal!==null?t-prevTotal:null;
                        const hasFile=!!(b.pdfUrl||b.pdfStoragePath||bidPdfCache.current.has(b.id));
                        const isActive=b.status==="Active";
                        return(
                          <div key={b.id} style={{background:T.bgPanel,border:`1px solid ${isActive?T.borderAccent+"44":T.borderPrimary}`,borderLeft:`3px solid ${isActive?T.borderAccent:T.borderSecondary}`,borderRadius:6,overflow:"hidden"}}>
                            {/* Main row */}
                            <div style={{display:"flex",alignItems:"center",gap:12,padding:"10px 14px"}}>
                              <div style={{width:40,textAlign:"center",flexShrink:0}}>
                                {hasFile?(
                                  <button onClick={()=>viewBidPdf(b)} title="Open PDF proposal"
                                    style={{fontSize:13,fontWeight:700,color:T.borderAccent,background:"none",border:"none",cursor:"pointer",padding:"2px 4px",borderRadius:3,fontFamily:FONT,textDecoration:"underline",textDecorationStyle:"dotted",textUnderlineOffset:2}}>
                                    v{b.version}
                                  </button>
                                ):(
                                  <div style={{fontSize:13,fontWeight:700,color:isActive?T.borderAccent:T.textMuted}}>v{b.version}</div>
                                )}
                              </div>
                              <div style={{flex:1,minWidth:0}}>
                                <div style={{fontSize:12,fontWeight:500,color:T.textPrimary}}>{fmt(t)}</div>
                                <div style={{fontSize:10,color:T.textWeak,marginTop:1}}>{b.date.slice(0,10)}{b.pdfName?` · ${b.pdfName}`:" · Manual entry"}</div>
                                <input
                                  className="ghost-input notes-input"
                                  value={b.notes||""}
                                  onChange={e=>setBids(prev=>prev.map(x=>x.id===b.id?{...x,notes:e.target.value}:x))}
                                  placeholder="Add a description…"
                                  style={{marginTop:3,width:"100%",fontStyle:b.notes?"italic":"normal"}}/>
                              </div>
                              {delta!==null&&(
                                <div style={{fontSize:11,fontWeight:600,fontVariantNumeric:"tabular-nums",
                                  color:delta<0?T.positive:delta>0?T.negative:T.textWeak}}>
                                  {delta<0?`▼ ${fmt(Math.abs(delta))}`:delta>0?`▲ ${fmt(delta)}`:"—"}
                                </div>
                              )}
                              <span style={{fontSize:10,padding:"2px 8px",borderRadius:10,fontWeight:600,flexShrink:0,
                                background:isActive?`${T.positive}22`:`${T.textMuted}18`,
                                color:isActive?T.positive:T.textMuted}}>
                                {isActive?"Active":"Archived"}
                              </span>
                              {/* Quick compare dropdown */}
                              {hasFile&&scopeBids.filter(x=>x.id!==b.id&&(x.pdfUrl||x.pdfStoragePath||bidPdfCache.current.has(x.id))).length>0&&(
                                <select defaultValue="" onChange={async e=>{
                                    const otherId=e.target.value;e.target.value="";
                                    if(otherId)await openBidPdfViewer([b.id,otherId]);
                                  }}
                                  style={{fontSize:10,padding:"2px 4px",borderRadius:3,border:`1px solid ${T.borderSecondary}`,background:T.bgInput,color:T.textWeak,cursor:"pointer",fontFamily:FONT,flexShrink:0}}>
                                  <option value="" disabled>⇔ vs…</option>
                                  {scopeBids.filter(x=>x.id!==b.id&&(x.pdfUrl||x.pdfStoragePath||bidPdfCache.current.has(x.id))).map(x=>{
                                    const xp=bidPartners.find(p=>p.id===x.partnerId);
                                    return <option key={x.id} value={x.id}>{xp?.name||"?"} v{x.version}</option>;
                                  })}
                                </select>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      );
    })()}

  </div>{/* end scrollable content */}
  </div>{/* end content flex column */}
</div>
);

}
